import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  MeasuringStrategy,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Lock,
  ExternalLink,
  RefreshCcw,
  AlertCircle,
  Inbox,
  Plus,
  Notebook,
  X,
  Loader2,
  Filter as FilterIcon,
  Bell,
  Users,
  Target,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  GripVertical,
} from "lucide-react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { useT, type Katalog } from "../i18n";
import { TaskDrawer } from "../components/TaskDrawer";
import TaskWizard from "../components/TaskWizard";
import FleetToggle from "../components/FleetToggle";
import { useModal, Modal } from "../ui/modal";
import { Button, EmptyState, projectDisplayName } from "../ui";
import IntakeStatusChip from "../components/intake/IntakeStatusChip";
import IntakeComposer from "../components/intake/IntakeComposer";
import IntakeQuestions from "../components/intake/IntakeQuestions";
import type { IntakeOut } from "../components/intake/types";

type ColumnId = "ingest" | "todo" | "in_progress" | "review" | "done";

interface PhaseOpt {
  id: string;
  name: string;
}

// Intake-Status wie ihn die „ingest"-Spalte liefert (Datenvertrag). Deckt sich
// mit IntakeStatus aus components/intake/types (ohne 'planned', das kommt hier
// nicht als Karte — nur raw|questions|answered|planning).
type IntakeCardStatus = "raw" | "questions" | "answered" | "planning";

interface KanbanCard {
  id: string;
  project_id: string | null;
  title: string;
  status: string | null;
  column: ColumnId;
  // Karten-Art (Datenvertrag): "intake" = Rohsprache-Karte der „ingest"-Spalte,
  // "task" (oder fehlend) = normale Aufgabe. Nur Task-Karten sind draggable.
  kind?: "task" | "intake";
  // Nur auf Intake-Karten gesetzt:
  intake_status?: IntakeCardStatus;
  open_questions_count?: number;
  start_date: string | null;
  due_date: string | null;
  owner: string | null;
  source: "local" | "salesforce";
  sf_url: string | null;
  read_only: boolean;
  phase_id: string | null;
  phase_name: string | null;
  phase_priority: number | null;
  sort_position: number;
  // Goal-driven Loop (Migration 0033)
  priority: number;
  goal: string | null;
  acceptance_criteria: string[] | null;
  // Sub-Task-Aggregat (Migration 0034)
  sub_count: number;
  subs_done: number;
  // Fremd-Zuweisung + Reminder (Migration 0057)
  created_by: string | null;
  delegatable: boolean;
  reminder_next_on: string | null;
  reminder_last_fired_on: string | null;
  // Review-Gate + Decider-Eskalation (Migration 0058)
  needs_human: boolean;
  review_round: number;
  // Task-Pipeline (Migration 0059): blocked = wartet auf unerfüllte Dependencies
  blocked: boolean;
  depends_on: string[];
}

interface AgentSessionLive {
  id: string;
  session_name: string;
  current_task_id: string | null;
  context_estimate_pct: number;
  tasks_done_count: number;
  suggested_action: "continue" | "wrap-up-soon" | "wrap-up-now";
  wall_time_minutes: number;
  last_heartbeat_at: string;
  scope_project_id: string | null;
  scope_external_slug: string | null;
}

interface KanbanLane {
  project_id: string;
  project_name: string;
  project_source: "local" | "salesforce";
  project_read_only: boolean;
  project_priority: number;
  cards: Record<ColumnId, KanbanCard[]>;
}

interface KanbanView {
  columns: ColumnId[];
  lanes: KanbanLane[];
  orphan_cards: Record<ColumnId, KanbanCard[]>;
}

/** Spalten-Beschriftungen in der aktiven Sprache. Funktion statt Konstante, weil
 *  der Text aus dem Katalog kommt (docs/i18n.md) — die Spalten-IDs selbst sind
 *  Daten und bleiben unübersetzt. */
const columnLabels = (t: Katalog): Record<ColumnId, string> => ({
  ingest: t.tasks.kanban.spalteIngest,
  todo: t.tasks.kanban.spalteOffen,
  in_progress: t.tasks.kanban.spalteInArbeit,
  review: t.tasks.kanban.spalteReview,
  done: t.tasks.kanban.spalteErledigt,
});

// Map column-id → local-task status value (sent to PATCH /tasks/{id}).
// „ingest" ist KEIN Task-Status — Intake-Karten sind agent/antwort-getrieben,
// nie per DnD dorthin/dorther verschiebbar. Kein Eintrag → jeder Drop-Pfad, der
// COLUMN_TO_TASK_STATUS[ingest] bräuchte, wird früher (in handleDragEnd) verworfen.
const COLUMN_TO_TASK_STATUS: Partial<Record<ColumnId, string>> = {
  todo: "open",
  in_progress: "in_progress",
  review: "review",
  done: "done",
};

function daysUntil(due: string | null): number | null {
  if (!due) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(due);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

// Kanban-Filter werden in localStorage persistiert (Backlog T1.5). Initial-
// Defaults: my=ON, done=OFF, sf=OFF — Today's-Work-View statt overwhelming
// all-projects-everything-Liste.
const FILTER_LS_KEY = "atlas.kanban.filters.v1";
type KanbanFilters = { includeDone: boolean; includeSf: boolean; myOnly: boolean };
const KANBAN_FILTER_DEFAULTS: KanbanFilters = { includeDone: false, includeSf: false, myOnly: true };

function readFilters(): KanbanFilters {
  try {
    const raw = localStorage.getItem(FILTER_LS_KEY);
    if (!raw) return KANBAN_FILTER_DEFAULTS;
    const parsed = JSON.parse(raw);
    return { ...KANBAN_FILTER_DEFAULTS, ...parsed };
  } catch {
    return KANBAN_FILTER_DEFAULTS;
  }
}

function writeFilters(f: KanbanFilters) {
  try { localStorage.setItem(FILTER_LS_KEY, JSON.stringify(f)); } catch { /* ignore quota */ }
}

// >=768px (Tailwind `md:`). Wird genutzt, um pro Lane ENTWEDER das Desktop-Grid
// ODER das Mobile-Tab-Layout zu rendern — NICHT beide gleichzeitig. Beide
// gleichzeitig (nur via CSS `hidden` getrennt) hatte doppelte dnd-kit-Droppable-/
// Sortable-Ids erzeugt: die versteckte Mobile-Kopie misst 0×0 und überschrieb
// die echten Rects der ersten Spalte → Drops auf „Offen" schlugen fehl.
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window === "undefined" ? true : window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

// Collapse-State der Workstream-Gruppen: Default = ZU, per-phase_id in localStorage
// gemerkt. Geteilt ueber alle Spalten, damit ein Workstream in Offen/In-Arbeit/Review
// synchron auf-/zuklappt. Gespeichert werden die OFFENEN phase_ids (leer = alle zu).
const WS_OPEN_KEY = "sb_kanban_ws_open";
function useCollapsedWorkstreams() {
  const [openSet, setOpenSet] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(WS_OPEN_KEY);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });
  const toggle = useCallback((pid: string) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      try {
        localStorage.setItem(WS_OPEN_KEY, JSON.stringify([...next]));
      } catch {
        /* localStorage nicht verfuegbar — Session-only ok */
      }
      return next;
    });
  }, []);
  const isOpen = useCallback(
    (pid: string | null) => (pid == null ? true : openSet.has(pid)),
    [openSet]
  );
  return { isOpen, toggle };
}

// Lane-DnD (Projekt-Reorder): Sortable-IDs der Lanes werden mit "lane:" praefixt,
// damit sie in handleDragEnd eindeutig von Card-Ids (rohe UUIDs) und Spalten-/
// Workstream-Droppables ("{lane}__{col}[…]") unterscheidbar sind.
const LANE_DND_PREFIX = "lane:";
const laneDndId = (projectId: string) => `${LANE_DND_PREFIX}${projectId}`;

// Prioritaets-Neuvergabe nach einem Lane-Drop. Verschiebt project_id `moved`
// vor `over` (bzw. ans Ende, wenn over == null) in der sichtbaren Reihenfolge
// `orderedIds` und vergibt 10, 15, 20, … (oberste sichtbare Lane = 10, jede
// weitere +5). Reine Funktion → isoliert testbar (Dry-Run im Task-Report).
// Rueckgabe: { projectId, priority } NUR fuer Lanes, deren Wert sich aendert.
const LANE_PRIORITY_BASE = 10;
const LANE_PRIORITY_STEP = 5;
function computeLaneReorder(
  orderedIds: string[],
  currentPriority: Record<string, number>,
  movedId: string,
  overId: string | null,
): { order: string[]; changes: { projectId: string; priority: number }[] } {
  const from = orderedIds.indexOf(movedId);
  if (from < 0) return { order: orderedIds, changes: [] };
  const without = orderedIds.filter((id) => id !== movedId);
  // Ziel-Index = Position von overId in der Rest-Liste; overId == null → ans Ende.
  let to = overId == null ? without.length : without.indexOf(overId);
  if (to < 0) to = without.length;
  const order = [...without.slice(0, to), movedId, ...without.slice(to)];
  const changes: { projectId: string; priority: number }[] = [];
  order.forEach((id, i) => {
    const next = LANE_PRIORITY_BASE + i * LANE_PRIORITY_STEP;
    if (currentPriority[id] !== next) changes.push({ projectId: id, priority: next });
  });
  return { order, changes };
}

// Generische 5er-Prioritaets-Neuvergabe fuer Workstreams (phase.priority) UND
// Tasks (task.priority) — dasselbe Muster wie computeLaneReorder, aber:
//   • ge-clamp-t auf PRIORITY_MAX (Schema erlaubt nur 1..100): sprengt 10+5·i
//     die Range (>18 Elemente), sitzt der Rest auf 100 statt PATCH-Reject.
//     Die SICHTBARE Reihenfolge bleibt heil, weil der Backend-Sort einen
//     stabilen Tiebreaker (position bzw. sort_position) hat.
//   • movedId verschiebt VOR overId (overId == null → ans Ende).
// Reine Funktion → isoliert testbar (Dry-Run im Task-Report).
const PRIORITY_BASE = 10;
const PRIORITY_STEP = 5;
const PRIORITY_MAX = 100;
function computePriorityReorder(
  orderedIds: string[],
  currentPriority: Record<string, number>,
  movedId: string,
  overId: string | null,
): { order: string[]; changes: { id: string; priority: number }[] } {
  const from = orderedIds.indexOf(movedId);
  if (from < 0) return { order: orderedIds, changes: [] };
  const without = orderedIds.filter((id) => id !== movedId);
  let to = overId == null ? without.length : without.indexOf(overId);
  if (to < 0) to = without.length;
  const order = [...without.slice(0, to), movedId, ...without.slice(to)];
  const changes: { id: string; priority: number }[] = [];
  order.forEach((id, i) => {
    const next = Math.min(PRIORITY_MAX, PRIORITY_BASE + i * PRIORITY_STEP);
    if (currentPriority[id] !== next) changes.push({ id, priority: next });
  });
  return { order, changes };
}

// Reine Neu-Nummerierung einer BEREITS geordneten Id-Liste (kein Verschieben):
// vergibt 10, 15, 20 … (Clamp 100) und gibt nur die Ids zurueck, deren priority
// sich aendert. Genutzt fuer AC2 (Task-Priority der Ziel-Gruppe), wo die neue
// visuelle Reihenfolge schon feststeht. Reine Funktion → Dry-Run-testbar.
function renumberPriorities(
  orderedIds: string[],
  currentPriority: Record<string, number>,
): { id: string; priority: number }[] {
  const changes: { id: string; priority: number }[] = [];
  orderedIds.forEach((id, i) => {
    const next = Math.min(PRIORITY_MAX, PRIORITY_BASE + i * PRIORITY_STEP);
    if (currentPriority[id] !== next) changes.push({ id, priority: next });
  });
  return changes;
}

// Workstream-DnD (Phase-Reorder): Sortable-IDs der Workstream-Header werden mit
// "phase:{laneId}__{col}__{phaseId}" praefixt. Der Header wird PRO Spalte
// gerendert (derselbe Workstream erscheint in Offen/In-Arbeit/Review); die
// Spalte im Key haelt die Sortable-Ids ueber die Spalten hinweg eindeutig
// (sonst 0×0-Rect-Clash wie beim Mobile-Layout-Bug). handleDragEnd verzweigt am
// "phase:"-Praefix VOR dem Card-Pfad.
const PHASE_DND_PREFIX = "phase:";
const phaseDndId = (laneId: string, col: ColumnId, phaseId: string) =>
  `${PHASE_DND_PREFIX}${laneId}__${col}__${phaseId}`;
function parsePhaseDndId(
  raw: string,
): { laneId: string; col: ColumnId; phaseId: string } | null {
  if (!raw.startsWith(PHASE_DND_PREFIX)) return null;
  const rest = raw.slice(PHASE_DND_PREFIX.length);
  // laneId + col via die ersten zwei "__"-Segmente; phaseId ist der Rest (kann
  // theoretisch keine "__" enthalten, aber wir sind robust).
  const parts = rest.split("__");
  if (parts.length < 3) return null;
  const [laneId, col, ...phaseParts] = parts;
  return { laneId, col: col as ColumnId, phaseId: phaseParts.join("__") };
}

export function KanbanBoard({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useT();
  const k = t.tasks.kanban;
  const { username } = useAuth();
  const [data, setData] = useState<KanbanView | null>(null);
  const { isOpen: isWsOpen, toggle: toggleWs } = useCollapsedWorkstreams();
  const initialFilters = useMemo(readFilters, []);
  const [includeDone, setIncludeDone] = useState(initialFilters.includeDone);
  const [includeSf, setIncludeSf] = useState(initialFilters.includeSf);
  const [myOnly, setMyOnly] = useState(initialFilters.myOnly);
  // „Von mir zugewiesen" (Migration 0057): zeigt nur Tasks, die ICH anderen
  // zugewiesen habe (created_by == ich, owner != ich). Nicht persistiert.
  const [assignedByMe, setAssignedByMe] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  // „+ Aufgabe hinzufügen" öffnet das volle TaskWizard-Modal (statt Inline-Zeile)
  // — damit ist die neue Aufgabe direkt zuweisbar (Punkt 5+7). Kontext trägt
  // Projekt (Lane), Default-Workstream und Ziel-Status (Spalte).
  const [createCtx, setCreateCtx] = useState<{
    projectId: string | null;
    phaseId: string | null;
    status: ColumnId;
  } | null>(null);
  const [createPhases, setCreatePhases] = useState<PhaseOpt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState("");
  const [drawerTask, setDrawerTask] = useState<{ id: string; source: "local" | "salesforce"; sf_url?: string | null } | null>(null);
  // Mobile-Filter-Toggle: Funnel-Icon im Header oeffnet inline-Section mit
  // allen Filtern. Defaults: my+done+sf folgen den persistierten Filters,
  // d.h. count startet bei 1 (my=ON), Badge ist sichtbar.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const activeFilterCount =
    (myOnly ? 1 : 0) +
    (assignedByMe ? 1 : 0) +
    (includeDone ? 1 : 0) +
    (includeSf ? 1 : 0) +
    (projectFilter.trim() ? 1 : 0);
  // Workstream-Context-Drawer (Migration 0035): zeigt + editiert phases.context_md.
  // Aufgerufen via Notebook-Icon im Kanban-Workstream-Header.
  const [workstreamCtxPhaseId, setWorkstreamCtxPhaseId] = useState<string | null>(null);

  // Maus: 6px-Schwelle, damit ein Klick (Drawer öffnen) nicht als Drag zählt.
  // Touch: 150ms Press-and-Hold + 8px-Toleranz — ein schnelles Wischen bleibt
  // Scrollen, erst gedrückt-Halten startet den Drag (Mobile-Scroll bleibt heil).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
  );
  // Aktiv gezogene Karte → wird im DragOverlay als „schwebende" Kopie gerendert.
  const [activeCard, setActiveCard] = useState<KanbanCard | null>(null);
  // Aktiv gezogene Lane (Projekt-Reorder) → project_id, für Overlay + Dim.
  const [activeLaneId, setActiveLaneId] = useState<string | null>(null);
  // Aktiv gezogener Workstream (Phase-Reorder) → { phaseId, name }, für Overlay.
  const [activePhase, setActivePhase] = useState<{ phaseId: string; name: string } | null>(null);
  const isDesktop = useIsDesktop();

  // Zeiger-basierte Kollision: der Droppable UNTER dem Cursor gewinnt (Karte >
  // Workstream-Gruppe > Spalte, weil pointerWithin nach Nähe zum Cursor sortiert).
  // Fallback rectIntersection nur, falls der Cursor in keinem Droppable liegt.
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const within = pointerWithin(args);
    return within.length > 0 ? within : rectIntersection(args);
  }, []);

  // Live-Sessions (Agent-Loops) — pollt /tasks/-/agent-sessions alle 30s.
  const [liveSessions, setLiveSessions] = useState<AgentSessionLive[]>([]);
  useEffect(() => {
    let cancelled = false;
    const fetchSessions = async () => {
      try {
        const list = await api.get<AgentSessionLive[]>("/tasks/-/agent-sessions");
        if (!cancelled) setLiveSessions(list);
      } catch {
        if (!cancelled) setLiveSessions([]);
      }
    };
    fetchSessions();
    const timer = setInterval(fetchSessions, 30_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);


  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set("include_done", String(includeDone));
      params.set("include_sf", String(includeSf));
      const v = await api.get<KanbanView>(`/views/kanban?${params}`);
      setData(v);
    } catch (e: any) {
      setError(e?.message || k.ladenFehlgeschlagen);
    } finally {
      setLoading(false);
    }
  }, [includeDone, includeSf]);

  useEffect(() => { load(); }, [load]);

  // Persist filter-Changes in localStorage.
  useEffect(() => {
    writeFilters({ includeDone, includeSf, myOnly });
  }, [includeDone, includeSf, myOnly]);

  // Apply both filters (project-name + myOnly) reactively
  const visibleData = useMemo(() => {
    if (!data) return null;
    const q = projectFilter.trim().toLowerCase();
    const filterCards = (cs: KanbanCard[]) => {
      // Intake-Karten (Ingest-Spalte) sind projekt-, nicht owner-gebunden — die
      // owner-basierten Filter (nur meine / von mir zugewiesen) lassen sie durch.
      if (assignedByMe) {
        return cs.filter(
          (c) => c.kind === "intake" || (c.created_by === username && c.owner !== username)
        );
      }
      return myOnly ? cs.filter((c) => c.kind === "intake" || c.owner === username) : cs;
    };
    const buildLane = (l: KanbanLane): KanbanLane => ({
      ...l,
      cards: Object.fromEntries(
        data.columns.map((c) => [c, filterCards(l.cards[c])])
      ) as Record<ColumnId, KanbanCard[]>,
    });
    const laneHasCards = (l: KanbanLane) =>
      data.columns.some((c) => l.cards[c].length > 0);
    const lanes = data.lanes
      .filter((l) => !q || l.project_name.toLowerCase().includes(q))
      .map(buildLane)
      // hide lanes whose cards-after-filter are all empty
      .filter(laneHasCards)
      // nach Projekt-Prioritaet (P0 zuerst -> P100), dann local vor SF, dann Name
      .sort((a, b) => {
        if (a.project_priority !== b.project_priority) {
          return a.project_priority - b.project_priority;
        }
        if (a.project_source !== b.project_source) {
          return a.project_source === "local" ? -1 : 1;
        }
        return a.project_name.localeCompare(b.project_name, "de");
      });
    const orphan = Object.fromEntries(
      data.columns.map((c) => [c, filterCards(data.orphan_cards[c])])
    ) as Record<ColumnId, KanbanCard[]>;
    return { ...data, lanes, orphan_cards: orphan };
  }, [data, projectFilter, myOnly, assignedByMe, username]);

  const visibleLanes = visibleData?.lanes ?? [];

  // Locate a card by id across all lanes + orphan. laneId = project_id der Lane
  // (bzw. "__orphan__") — nötig, um Cross-Workstream nur innerhalb desselben
  // Projekts (= derselben Lane) zuzulassen.
  const findCardLocation = (
    view: KanbanView,
    cardId: string,
  ): { card: KanbanCard; col: ColumnId; cards: KanbanCard[]; idx: number; laneId: string } | null => {
    for (const lane of view.lanes) {
      for (const col of view.columns) {
        const idx = lane.cards[col].findIndex((c) => c.id === cardId);
        if (idx >= 0) return { card: lane.cards[col][idx], col, cards: lane.cards[col], idx, laneId: lane.project_id };
      }
    }
    for (const col of view.columns) {
      const idx = view.orphan_cards[col].findIndex((c) => c.id === cardId);
      if (idx >= 0) return { card: view.orphan_cards[col][idx], col, cards: view.orphan_cards[col], idx, laneId: "__orphan__" };
    }
    return null;
  };

  // Kartenspalte einer Lane (oder Orphan) holen.
  const laneCardsFor = (view: KanbanView, laneId: string, col: ColumnId): KanbanCard[] | null => {
    if (laneId === "__orphan__") return view.orphan_cards[col];
    return view.lanes.find((l) => l.project_id === laneId)?.cards[col] ?? null;
  };

  // Compute new sort_position to slot between neighbors at targetIdx in cards.
  // cards array is the destination column AFTER the active card was removed.
  const midpointPosition = (cards: KanbanCard[], targetIdx: number): number => {
    if (cards.length === 0) return 100;
    if (targetIdx <= 0) return cards[0].sort_position - 100;
    if (targetIdx >= cards.length) return cards[cards.length - 1].sort_position + 100;
    const prev = cards[targetIdx - 1].sort_position;
    const next = cards[targetIdx].sort_position;
    if (next - prev <= 1) {
      // Collision — caller will trigger reload + renumber roundtrip. For now,
      // return midpoint as float-rounded; backend stores int (might equal a neighbor,
      // but ORDER BY uses stable secondary sort so it's safe until next reload).
      return Math.round((prev + next) / 2);
    }
    return Math.round((prev + next) / 2);
  };

  // Geordnete, reale Workstreams (phase_id != null) einer Lane-Spalte in
  // Backend-Reihenfolge (= ph.priority NULLS LAST, ph.position). Nur echte
  // Workstreams sind reorderbar; die „ohne Workstream"-Pseudo-Gruppe (phase_id
  // null) nicht. Rueckgabe je Workstream: id + name + aktuelle priority.
  const orderedPhasesFor = (
    view: KanbanView,
    laneId: string,
    col: ColumnId,
  ): { id: string; name: string | null; priority: number | null }[] => {
    const arr = laneCardsFor(view, laneId, col);
    if (!arr) return [];
    const out: { id: string; name: string | null; priority: number | null }[] = [];
    const seen = new Set<string>();
    for (const c of arr) {
      const pid = c.phase_id;
      if (pid == null || seen.has(pid)) continue;
      seen.add(pid);
      out.push({ id: pid, name: c.phase_name, priority: c.phase_priority });
    }
    return out;
  };

  // Workstream-Reorder (phase.priority per DnD, AC1): oberster Workstream der
  // Spalte = 10, jeder weitere +5 (Clamp 100). Backend sortiert ph.priority
  // NULLS LAST, ph.position — niedrigere priority = weiter oben, identisch zum
  // Lane-Sort. Persistiert via PATCH /phases/{id}; optimistisch schreiben wir
  // phase_priority auf ALLE Karten des Workstreams (in allen Spalten), damit die
  // Reihenfolge schon vor dem Reload haelt und nach Reload stabil bleibt.
  const handlePhaseDragEnd = async (
    movedRaw: string,
    overRaw: string | undefined,
  ) => {
    setActivePhase(null);
    if (!data) return;
    const moved = parsePhaseDndId(movedRaw);
    if (!moved) return;
    const { laneId, col, phaseId: movedPhaseId } = moved;
    const ordered = orderedPhasesFor(data, laneId, col);
    const orderedIds = ordered.map((p) => p.id);
    if (orderedIds.length < 2 || !orderedIds.includes(movedPhaseId)) return;

    // overId auf einen Ziel-Workstream normalisieren. Nur Drops auf einen
    // anderen Workstream-Header derselben Lane+Spalte zaehlen; alles andere
    // (Karte, Spalte, fremde Lane) → ans Ende der Workstream-Liste.
    let overPhaseId: string | null = null;
    if (overRaw) {
      const overParsed = parsePhaseDndId(overRaw);
      if (overParsed && overParsed.laneId === laneId && overParsed.col === col) {
        overPhaseId = overParsed.phaseId;
      }
    }
    if (overPhaseId && !orderedIds.includes(overPhaseId)) overPhaseId = null;
    if (overPhaseId === movedPhaseId) return;

    const currentPriority: Record<string, number> = {};
    ordered.forEach((p) => {
      // NULL-priority (noch nie gesetzt) als „ganz unten" behandeln, damit ein
      // Change garantiert geschrieben wird.
      currentPriority[p.id] = p.priority ?? PRIORITY_MAX + 1;
    });
    const { changes } = computePriorityReorder(
      orderedIds, currentPriority, movedPhaseId, overPhaseId,
    );
    if (changes.length === 0) return;

    // Optimistic: phase_priority auf allen Karten dieser Phasen (alle Spalten)
    // aktualisieren → groupByWorkstream/Backend-Sort spiegelt die neue Ordnung.
    const changeMap = new Map(changes.map((c) => [c.id, c.priority]));
    setData((d) => {
      if (!d) return d;
      const bump = (cards: KanbanCard[]) =>
        cards.map((c) =>
          c.phase_id && changeMap.has(c.phase_id)
            ? { ...c, phase_priority: changeMap.get(c.phase_id)! }
            : c,
        );
      return {
        ...d,
        lanes: d.lanes.map((l) =>
          l.project_id === laneId
            ? {
                ...l,
                cards: Object.fromEntries(
                  d.columns.map((c) => [c, bump(l.cards[c])]),
                ) as Record<ColumnId, KanbanCard[]>,
              }
            : l,
        ),
      };
    });

    try {
      await Promise.all(
        changes.map((c) => api.patch(`/phases/${c.id}`, { priority: c.priority })),
      );
      // Backend-Sort (ph.priority) wirkt erst nach Reload voll — Karten sind
      // aktuell nur optimistisch umgeschrieben, aber nicht neu gruppiert.
      await load();
    } catch (e: any) {
      setError(k.workstreamReihenfolgeFehler(e?.message || "?"));
      await load();
    }
  };

  // Lane-Reorder (Projekt-Prioritaet per DnD): oberste sichtbare Lane = 10,
  // jede weitere +5; nur Lanes deren priority sich aendert werden per
  // PATCH /projects/{id} persistiert. Optimistic update auf data.lanes (der
  // visibleData-Memo re-sortiert daraufhin nach project_priority), bei Fehler
  // reload(). Reihenfolge bleibt nach Reload, weil priority persistiert ist.
  const handleLaneDragEnd = async (movedProjectId: string, rawOverId: string | undefined) => {
    setActiveLaneId(null);
    if (!data) return;
    // Sichtbare, reorderbare Lanes in Anzeige-Reihenfolge (SF/Orphan sind es nicht).
    const orderedIds = visibleLanes
      .filter((l) => l.project_source === "local" && l.project_id !== "__orphan__")
      .map((l) => l.project_id);
    if (orderedIds.length < 2 || !orderedIds.includes(movedProjectId)) return;

    // overId auf eine Ziel-Lane normalisieren: entweder ein "lane:"-Sortable
    // oder ein Card/Column-Droppable ("{laneId}__…") — beides traegt die
    // Ziel-project_id. Unbekannt/leer → ans Ende.
    let overProjectId: string | null = null;
    if (rawOverId) {
      if (rawOverId.startsWith(LANE_DND_PREFIX)) {
        overProjectId = rawOverId.slice(LANE_DND_PREFIX.length);
      } else if (rawOverId.includes("__")) {
        overProjectId = rawOverId.split("__")[0];
      } else {
        // Drop auf eine Karte → deren Lane als Ziel.
        const loc = findCardLocation(data, rawOverId);
        overProjectId = loc?.laneId ?? null;
      }
    }
    // Ziel muss eine reorderbare Lane sein; sonst ans Ende.
    if (overProjectId && !orderedIds.includes(overProjectId)) overProjectId = null;
    if (overProjectId === movedProjectId) return;

    const currentPriority: Record<string, number> = {};
    visibleLanes.forEach((l) => { currentPriority[l.project_id] = l.project_priority; });
    const { changes } = computeLaneReorder(
      orderedIds, currentPriority, movedProjectId, overProjectId,
    );
    if (changes.length === 0) return;

    // Optimistic: neue Prioritaeten in data.lanes schreiben → Memo re-sortiert.
    const changeMap = new Map(changes.map((c) => [c.projectId, c.priority]));
    setData((d) => {
      if (!d) return d;
      return {
        ...d,
        lanes: d.lanes.map((l) =>
          changeMap.has(l.project_id)
            ? { ...l, project_priority: changeMap.get(l.project_id)! }
            : l,
        ),
      };
    });

    try {
      // Einzel-PATCH je geaenderter Lane (kein Batch-Endpoint noetig).
      await Promise.all(
        changes.map((c) =>
          api.patch(`/projects/${c.projectId}`, { priority: c.priority }),
        ),
      );
    } catch (e: any) {
      setError(k.reihenfolgeFehler(e?.message || "?"));
      await load();
    }
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    const activeId = e.active.id as string;
    // Lane-Drag (Projekt-Reorder) → eigener Pfad. Praefix "lane:" grenzt es
    // eindeutig von Card-Drags (rohe UUID) ab.
    if (activeId.startsWith(LANE_DND_PREFIX)) {
      await handleLaneDragEnd(activeId.slice(LANE_DND_PREFIX.length), e.over?.id as string | undefined);
      return;
    }
    // Workstream-Drag (Phase-Reorder, AC1) → eigener Pfad, VOR dem Card-Pfad.
    // Praefix "phase:" grenzt es von Card-Drags (rohe UUID) + Lane-Drags ab.
    if (activeId.startsWith(PHASE_DND_PREFIX)) {
      await handlePhaseDragEnd(activeId, e.over?.id as string | undefined);
      return;
    }
    setActiveCard(null);
    if (!data) return;
    const cardId = activeId;
    const overId = e.over?.id as string | undefined;
    if (!overId || overId === cardId) return;

    const src = findCardLocation(data, cardId);
    if (!src) return;
    // Intake-Karten (Datenvertrag kind:"intake") sind agent/antwort-getrieben —
    // sie tragen gar keine dnd-Listener, aber als Absicherung nie verschieben.
    if (src.card.kind === "intake") return;
    if (src.card.read_only) {
      setError(k.sfNichtVerschiebbar);
      setTimeout(() => setError(null), 4000);
      return;
    }

    // overId ist eine von vier Formen:
    //   "phase:{lane}__{col}__{phaseId}"      → Workstream-HEADER (Sortable) →
    //                                            wie ein Workstream-Drop dieser Phase
    //   "{lane}__{col}__ws__{phaseId|none}"  → Workstream-Dropzone (setzt phase_id)
    //   "{lane}__{col}"                       → Spalten-Dropzone (ans Ende anhängen)
    //   "{cardId}"                            → Sortable-Karte (davor einsortieren)
    let destLaneId: string;
    let destCol: ColumnId;
    // phase des Ziels: string = konkreter Workstream, null = „ohne Workstream",
    // "__keep__" = Workstream der Quelle beibehalten (Spalten-Drop).
    let destPhase: string | null | "__keep__";
    let insertBeforeCardId: string | null = null;
    // Wenn gesetzt (auch null), ans Ende der Karten dieser Phase einsortieren.
    let groupPhaseForInsert: string | null | undefined;

    // Karte auf einen Workstream-HEADER (phase:-Sortable) fallen gelassen: als
    // Workstream-Drop in diese Phase behandeln (sonst wuerde overId.split("__")
    // den "phase:"-Praefix in die laneId ziehen → Drop still verworfen).
    const overPhaseHeader = parsePhaseDndId(overId);
    if (overPhaseHeader) {
      if (!data.columns.includes(overPhaseHeader.col)) return;
      destLaneId = overPhaseHeader.laneId;
      destCol = overPhaseHeader.col;
      destPhase = overPhaseHeader.phaseId;
      groupPhaseForInsert = destPhase;
    } else if (overId.includes("__ws__")) {
      const [prefix, phasePart] = overId.split("__ws__");
      const [laneId, colStr] = prefix.split("__") as [string, ColumnId];
      if (!data.columns.includes(colStr)) return;
      destLaneId = laneId;
      destCol = colStr;
      destPhase = phasePart === "none" ? null : phasePart;
      groupPhaseForInsert = destPhase;
    } else if (overId.includes("__")) {
      const [laneId, colStr] = overId.split("__") as [string, ColumnId];
      if (!data.columns.includes(colStr)) return;
      destLaneId = laneId;
      destCol = colStr;
      destPhase = "__keep__";
    } else {
      const dst = findCardLocation(data, overId);
      if (!dst) return;
      destLaneId = dst.laneId;
      destCol = dst.col;
      destPhase = dst.card.phase_id;
      insertBeforeCardId = overId;
    }

    // Drops IN die „ingest"-Spalte ignorieren: dort leben nur Intake-Karten,
    // keine Aufgabe darf dorthin wandern (kein Task-Status „ingest").
    const newStatus = COLUMN_TO_TASK_STATUS[destCol];
    if (destCol === "ingest" || !newStatus) return;

    const destColArr = laneCardsFor(data, destLaneId, destCol);
    if (!destColArr) return;

    const sameLane = destLaneId === src.laneId;
    const statusChanged = newStatus !== src.card.status;
    // Workstream nur INNERHALB desselben Projekts (= Lane) umhängen — projekt-
    // übergreifend bräuchte es einen project_id-Wechsel (nicht in diesem Scope).
    const resolvedPhase = destPhase === "__keep__" ? src.card.phase_id : destPhase;
    const phaseChanged = sameLane && resolvedPhase !== src.card.phase_id;

    // Ziel-Index in der Zielspalte bestimmen.
    const resolveIdx = (arr: KanbanCard[]): number => {
      if (insertBeforeCardId) {
        const i = arr.findIndex((c) => c.id === insertBeforeCardId);
        return i >= 0 ? i : arr.length;
      }
      if (groupPhaseForInsert !== undefined) {
        let last = -1;
        arr.forEach((c, i) => { if ((c.phase_id ?? null) === groupPhaseForInsert) last = i; });
        return last >= 0 ? last + 1 : arr.length;
      }
      return arr.length;
    };
    const targetIdx = resolveIdx(destColArr);

    const sameColArr = destColArr === src.cards;
    const samePosition =
      sameColArr &&
      !statusChanged &&
      !phaseChanged &&
      (targetIdx === src.idx || targetIdx === src.idx + 1);
    if (samePosition) return;

    // Cards-array ohne src-Karte, für die Midpoint-Berechnung.
    const reindexed = sameColArr
      ? [...destColArr.slice(0, src.idx), ...destColArr.slice(src.idx + 1)]
      : destColArr;
    const adjustedIdx = sameColArr && targetIdx > src.idx ? targetIdx - 1 : targetIdx;
    const newSortPos = midpointPosition(reindexed, adjustedIdx);

    // AC2 — Task-Priority in 5er-Schritten fuer die Geschwister im ZIEL (Spalte +
    // Workstream). Wir rekonstruieren die Ziel-Spalte NACH dem Drop (reindexed +
    // Einfuegen der bewegten Karte an targetIdx), filtern auf die Ziel-Phase und
    // vergeben 10, 15, 20 … (Clamp 100) in dieser sichtbaren Reihenfolge.
    // Gilt fuer alle Karten-Drops in eine echte Lane — auch Cross-Status/Cross-
    // Workstream (AC3): dort ordnet es die Ziel-Gruppe frisch, was gewollt ist.
    // task.priority ist der PRIMAERE In-Phase-Sortkey (vor sort_position), also
    // wird die per-priority-Ordnung nach Reload autoritativ.
    const movedGhost: KanbanCard = { ...src.card, phase_id: resolvedPhase };
    const destAfter = [
      ...reindexed.slice(0, adjustedIdx),
      movedGhost,
      ...reindexed.slice(adjustedIdx),
    ];
    // Ziel-Gruppe = Karten der Ziel-Spalte mit derselben (aufgeloesten) phase_id.
    const destGroup = destAfter.filter(
      (c) => (c.phase_id ?? null) === (resolvedPhase ?? null),
    );
    const prioOrderedIds = destGroup.map((c) => c.id);
    const prioCurrent: Record<string, number> = {};
    destGroup.forEach((c) => {
      // Die bewegte Karte bekommt „current" absichtlich so, dass sich garantiert
      // ein Change ergibt, falls ihre Zielposition eine neue Prio verlangt.
      prioCurrent[c.id] = c.id === cardId ? -1 : c.priority;
    });
    // movedId steht schon an seiner Zielposition in prioOrderedIds → overId=null
    // (kein Verschieben mehr), computePriorityReorder vergibt nur neu durch.
    const prioReindex = renumberPriorities(prioOrderedIds, prioCurrent);

    // Optimistic update
    setData((d) => {
      if (!d) return d;
      const fresh = JSON.parse(JSON.stringify(d)) as KanbanView;
      const s = findCardLocation(fresh, cardId);
      if (!s) return fresh;
      const [moved] = s.cards.splice(s.idx, 1);
      moved.column = destCol;
      moved.status = newStatus;
      moved.sort_position = newSortPos;
      if (phaseChanged) moved.phase_id = resolvedPhase;
      const destArr = laneCardsFor(fresh, destLaneId, destCol);
      if (!destArr) { fresh.orphan_cards[destCol].push(moved); return fresh; }
      destArr.splice(resolveIdx(destArr), 0, moved);
      // Neue Task-Prioritaeten der Ziel-Gruppe optimistisch schreiben.
      const prioMap = new Map(prioReindex.map((c) => [c.id, c.priority]));
      destArr.forEach((c) => { if (prioMap.has(c.id)) c.priority = prioMap.get(c.id)!; });
      return fresh;
    });

    try {
      const prioMap = new Map(prioReindex.map((c) => [c.id, c.priority]));
      const movedPatch: {
        status?: string;
        sort_position: number;
        phase_id?: string | null;
        priority?: number;
      } = { sort_position: newSortPos };
      if (statusChanged) movedPatch.status = newStatus;
      if (phaseChanged) movedPatch.phase_id = resolvedPhase;
      if (prioMap.has(cardId)) movedPatch.priority = prioMap.get(cardId)!;
      // PATCH der bewegten Karte + separate PATCHes fuer die uebrigen Geschwister,
      // deren priority sich durch die Neuvergabe verschoben hat.
      await Promise.all([
        api.patch(`/tasks/${cardId}`, movedPatch),
        ...prioReindex
          .filter((c) => c.id !== cardId)
          .map((c) => api.patch(`/tasks/${c.id}`, { priority: c.priority })),
      ]);
    } catch (e: any) {
      setError(k.speichernFehler(e?.message || "?"));
      await load();
    }
  };

  const handleDragStart = (e: DragStartEvent) => {
    if (!data) return;
    const id = e.active.id as string;
    if (id.startsWith(LANE_DND_PREFIX)) {
      setActiveLaneId(id.slice(LANE_DND_PREFIX.length));
      setActiveCard(null);
      setActivePhase(null);
      return;
    }
    if (id.startsWith(PHASE_DND_PREFIX)) {
      const parsed = parsePhaseDndId(id);
      if (parsed) {
        const ordered = orderedPhasesFor(data, parsed.laneId, parsed.col);
        const p = ordered.find((x) => x.id === parsed.phaseId);
        setActivePhase({ phaseId: parsed.phaseId, name: p?.name ?? k.workstreamFallback });
      }
      setActiveCard(null);
      setActiveLaneId(null);
      return;
    }
    const loc = findCardLocation(data, id);
    setActiveCard(loc?.card ?? null);
    setActivePhase(null);
  };

  // Tasks ohne Projekt werden im Kanban NICHT angezeigt (analog zu leeren
  // Projekt-Lanes) — die „Ohne Projekt"/__orphan__-Lane entfaellt. Die Task-Daten
  // bleiben unberuehrt (weiter ueber /tasks etc. erreichbar), nur die Kanban-
  // Darstellung blendet sie aus. Konstant false → Orphan-Lane rendert nie, und
  // der Empty-State (kein sichtbares Projekt) greift korrekt.
  const orphanHasCards = false;

  // „+ Aufgabe hinzufügen" → volles TaskWizard-Modal öffnen (Punkt 7). Lädt die
  // Workstreams des Projekts, damit der Workstream-Select im Modal befüllt ist,
  // und merkt sich Projekt/Default-Phase/Ziel-Status.
  const openCreateWizard = useCallback(
    async (project_id: string | null, phase_id: string | null, column: ColumnId) => {
      const pid = project_id && project_id !== "__orphan__" ? project_id : null;
      setCreateCtx({ projectId: pid, phaseId: phase_id, status: column });
      setCreatePhases([]);
      if (pid) {
        try {
          setCreatePhases(await api.get<PhaseOpt[]>(`/projects/${pid}/phases`));
        } catch {
          setCreatePhases([]);
        }
      }
    },
    []
  );

  return (
    <div className="p-3 md:p-6 max-w-full">
      {/* Mobile-Header: Title + Funnel + Reload */}
      <div className="md:hidden flex items-center gap-2 mb-3">
        {embedded ? (
          <div className="flex-1" />
        ) : (
          <h1 className="text-xl font-display font-semibold flex-1">{k.titel}</h1>
        )}
        <button
          onClick={() => setAssignOpen(true)}
          className="flex items-center justify-center min-h-11 min-w-11 rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700"
          title={k.aufgabeZuweisen}
          aria-label={k.aufgabeZuweisen}
        >
          <Users className="w-5 h-5" />
        </button>
        <button
          onClick={() => setMobileFiltersOpen((o) => !o)}
          className={clsx(
            "relative flex items-center justify-center min-h-11 min-w-11 rounded-md transition-colors",
            mobileFiltersOpen
              ? "bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
              : "hover:bg-cream-200/60 dark:hover:bg-ink-700"
          )}
          title={k.filter}
          aria-label={k.filterUmschalten}
          aria-expanded={mobileFiltersOpen}
        >
          <FilterIcon className="w-5 h-5" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-500 text-white text-[10px] font-semibold tabular-nums flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
        <button
          onClick={load}
          disabled={loading}
          className="rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 flex items-center justify-center min-h-11 min-w-11"
          title={k.neuLaden}
          aria-label={k.neuLaden}
        >
          <RefreshCcw className={clsx("w-4 h-4", loading && "animate-spin")} />
        </button>
      </div>

      {/* Mobile-Filter-Section: inline-collapse, nur wenn open */}
      {mobileFiltersOpen && (
        <div className="md:hidden mb-3 p-3 rounded-md border border-cream-200 dark:border-ink-700 bg-cream-50/60 dark:bg-ink-800/40 space-y-2">
          <input
            type="text"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            placeholder={k.projektFiltern}
            aria-label={k.projektFilter}
            className="w-full px-2 py-2 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm"
          />
          <div className="flex flex-wrap gap-1.5">
            <KanbanFilterPill label={k.pilleMeineTasks} active={myOnly} onClick={() => setMyOnly(!myOnly)} />
            <KanbanFilterPill label={k.pilleVonMirZugewiesen} active={assignedByMe} onClick={() => setAssignedByMe(!assignedByMe)} />
            <KanbanFilterPill label={k.pilleDone} active={includeDone} onClick={() => setIncludeDone(!includeDone)} />
            <KanbanFilterPill label={k.pilleSalesforce} active={includeSf} onClick={() => setIncludeSf(!includeSf)} />
          </div>
        </div>
      )}

      {/* Desktop-Header: inline Filter-Toolbar unveraendert */}
      <div className="hidden md:flex items-center gap-3 mb-4 flex-wrap">
        {!embedded && <h1 className="text-2xl font-display font-semibold">{k.titel}</h1>}
        <div className="ml-auto flex items-center gap-2 flex-wrap text-sm">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setAssignOpen(true)}
            icon={<Users className="w-3.5 h-3.5" />}
          >
            {k.zuweisen}
          </Button>
          <input
            type="text"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            placeholder={k.projektFiltern}
            aria-label={k.projektFilter}
            className="px-2 py-1 rounded-md border border-cream-200 dark:border-ink-600 bg-cream-50 dark:bg-ink-800 text-xs"
          />
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" className="w-3 h-3" checked={myOnly} onChange={(e) => setMyOnly(e.target.checked)} />
            {k.nurMeine}
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" title={k.vonMirZugewiesenTitel}>
            <input type="checkbox" className="w-3 h-3" checked={assignedByMe} onChange={(e) => setAssignedByMe(e.target.checked)} />
            {k.vonMirZugewiesen}
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" className="w-3 h-3" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
            {k.erledigte}
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" className="w-3 h-3" checked={includeSf} onChange={(e) => setIncludeSf(e.target.checked)} />
            {k.salesforce}
          </label>
          <button onClick={load} disabled={loading} className="p-1.5 rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700" title={k.neuLaden} aria-label={k.neuLaden}>
            <RefreshCcw className={clsx("w-4 h-4", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-soft border border-danger/30 rounded-md px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {data && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => { setActiveCard(null); setActiveLaneId(null); setActivePhase(null); }}
        >
          {/* Column headers (sticky) — desktop only; mobile uses per-Lane Tab-Bar */}
          <div
            className="hidden md:grid sticky top-0 z-10 bg-cream-50 dark:bg-ink-900 pb-2 pt-1"
            style={{ gridTemplateColumns: `200px repeat(${data.columns.length}, minmax(260px, 1fr))` }}
          >
            <div className="text-xs uppercase tracking-wider text-ink-50/50 px-2">
              {k.spalteProjekt}
            </div>
            {data.columns.map((c) => (
              <div key={c} className="text-xs uppercase tracking-wider text-ink-50/50 px-2">
                {columnLabels(t)[c]}
              </div>
            ))}
          </div>

          {/* Swimlanes. Reorderbare (lokale, echte) Projekt-Lanes bilden einen
              vertikalen SortableContext — Drag am Grip-Handle vergibt priority
              in 5er-Schritten neu (oberste = 10). SF- und Orphan-Lanes sind NICHT
              Teil des Contexts (nicht sortierbar). */}
          <div className="space-y-1">
            <SortableContext
              items={visibleLanes
                .filter((l) => l.project_source === "local" && l.project_id !== "__orphan__")
                .map((l) => laneDndId(l.project_id))}
              strategy={verticalListSortingStrategy}
            >
            {visibleLanes.map((lane) => {
              // Aktive Sessions in dieser Lane = Sessions die ENTWEDER
              // einen Token-Scope auf das Projekt haben ODER gerade an einer
              // Task in der Lane arbeiten. Token-Scope-Bindung haelt die Card
              // sichtbar auch zwischen Tasks (current_task_id NULL).
              const cardIdsInLane = new Set<string>();
              data.columns.forEach((c) => lane.cards[c].forEach((card) => cardIdsInLane.add(card.id)));
              const laneSessions = liveSessions.filter(
                (s) =>
                  s.scope_project_id === lane.project_id ||
                  (s.current_task_id && cardIdsInLane.has(s.current_task_id))
              );
              return (
                <Lane
                  key={lane.project_id}
                  lane={lane}
                  columns={data.columns}
                  isDesktop={isDesktop}
                  liveSessions={laneSessions}
                  sortable={lane.project_source === "local" && lane.project_id !== "__orphan__"}
                  dimmed={activeLaneId === lane.project_id}
                  onCardClick={(c) => setDrawerTask({ id: c.id, source: c.source, sf_url: c.sf_url })}
                  onOpenCreate={openCreateWizard}
                  onIntakeChanged={load}
                  onWorkstreamContextOpen={setWorkstreamCtxPhaseId}
                  isWsOpen={isWsOpen}
                  onToggleWs={toggleWs}
                />
              );
            })}
            </SortableContext>
            {orphanHasCards && visibleData && (
              <Lane
                lane={{
                  project_id: "__orphan__",
                  project_name: "(no project)",
                  project_source: "local",
                  project_read_only: false,
                  project_priority: 100,
                  cards: visibleData.orphan_cards,
                }}
                columns={data.columns}
                isDesktop={isDesktop}
                onCardClick={(c) => setDrawerTask({ id: c.id, source: c.source, sf_url: c.sf_url })}
                onOpenCreate={openCreateWizard}
                onIntakeChanged={load}
                onWorkstreamContextOpen={setWorkstreamCtxPhaseId}
                isWsOpen={isWsOpen}
                onToggleWs={toggleWs}
              />
            )}
            {!visibleLanes.length && !orphanHasCards && (
              (() => {
                // Liegt Leere nur am „Nur meine"-Filter? Dann Ein-Klick-Ausweg
                // statt Sackgasse.
                const rawHasCards =
                  !!data &&
                  (data.lanes.some((l) =>
                    data.columns.some((c) => l.cards[c].length > 0),
                  ) ||
                    data.columns.some((c) => data.orphan_cards[c].length > 0));
                if (myOnly && rawHasCards) {
                  return (
                    <EmptyState
                      icon={<Inbox className="w-6 h-6" />}
                      title={k.leerEigeneTitel}
                      hint={k.leerEigeneHinweis}
                      action={
                        <Button variant="secondary" onClick={() => setMyOnly(false)}>
                          {k.alleAufgabenZeigen}
                        </Button>
                      }
                    />
                  );
                }
                return (
                  <EmptyState
                    icon={<Inbox className="w-6 h-6" />}
                    title={
                      projectFilter
                        ? k.leerGefiltertTitel(projectFilter)
                        : t.tasks.keineAufgaben
                    }
                    hint={k.leerHinweis}
                    action={
                      projectFilter ? (
                        <Button
                          variant="secondary"
                          onClick={() => setProjectFilter("")}
                        >
                          {k.filterZuruecksetzen}
                        </Button>
                      ) : undefined
                    }
                  />
                );
              })()
            )}
          </div>
          <DragOverlay dropAnimation={null}>
            {activeCard ? (
              <div style={{ width: 260 }}>
                <CardView card={activeCard} overlay />
              </div>
            ) : activeLaneId ? (
              (() => {
                const l = visibleLanes.find((x) => x.project_id === activeLaneId);
                if (!l) return null;
                return (
                  <div className="flex items-center gap-1.5 rounded-md border border-brand-300 bg-surface dark:bg-ink-800 shadow-lg px-2 py-1.5 cursor-grabbing">
                    <GripVertical className="w-3.5 h-3.5 text-brand-500 shrink-0" />
                    <span className="text-sm font-medium text-ink truncate">
                      {projectDisplayName(l.project_name)}
                    </span>
                    {l.project_priority !== 50 && <PriorityChip priority={l.project_priority} />}
                  </div>
                );
              })()
            ) : activePhase ? (
              <div className="flex items-center gap-1.5 rounded-md border border-brand-300 bg-brand-100/80 dark:bg-brand-900/40 shadow-lg px-2 py-1 cursor-grabbing">
                <GripVertical className="w-3.5 h-3.5 text-brand-500 shrink-0" />
                <span className="text-[11px] uppercase tracking-wide font-bold text-brand-800 dark:text-brand-200 truncate">
                  {activePhase.name}
                </span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <TaskDrawer
        taskInfo={drawerTask}
        onClose={() => setDrawerTask(null)}
        onChanged={load}
      />

      {/* „+ Aufgabe hinzufügen" (Punkt 7): volles TaskWizard-Modal statt Inline-
          Zeile — im Create-Mode enthält es das „Zuweisen an"-Feld (Punkt 5),
          Reminder + Anhänge. projectId/phaseId/Status kommen aus der Spalte. */}
      {createCtx && (
        <TaskWizard
          open={true}
          onClose={() => setCreateCtx(null)}
          projectId={createCtx.projectId}
          defaultPhaseId={createCtx.phaseId}
          defaultStatus={COLUMN_TO_TASK_STATUS[createCtx.status]}
          phases={createPhases}
          onCreated={() => {
            setCreateCtx(null);
            load();
          }}
          onAssigned={() => {
            setCreateCtx(null);
            setAssignedByMe(true);
            load();
          }}
        />
      )}

      {/* Globaler Zuweisungs-Flow (Migration 0057): projektfreie Aufgabe an
          mehrere Mitarbeiter, mit Reminder + Anhängen. */}
      <TaskWizard
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        projectId={null}
        defaultPhaseId={null}
        phases={[]}
        onAssigned={() => {
          setAssignOpen(false);
          setAssignedByMe(true);
          load();
        }}
        onCreated={() => {
          setAssignOpen(false);
          load();
        }}
      />

      <WorkstreamContextDrawer
        phaseId={workstreamCtxPhaseId}
        onClose={() => setWorkstreamCtxPhaseId(null)}
      />
    </div>
  );
}

function KanbanFilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        "min-h-11 px-3 rounded-full text-xs font-medium border transition-colors",
        active
          ? "bg-brand-500 text-white border-brand-500"
          : "bg-cream-50 dark:bg-ink-800 text-ink-50 border-cream-300 dark:border-ink-600"
      )}
    >
      {label}
    </button>
  );
}

interface WorkstreamGoal {
  phase_id: string;
  name: string;
  priority: number;
  overarching_goal: string | null;
}
interface ProjectGoals {
  project_id: string;
  goal_md: string | null;
  architecture_md: string | null;
  fleet_enabled: boolean;
  workstreams: WorkstreamGoal[];
}

// Ziele-Panel (Migration 0060): lazy-geladenes Projekt-Overarching-Goal (editierbar)
// + Workstream-Ziele (read-only, aus phases.context_md ## Overarching Goal).
function GoalsPanel({ projectId }: { projectId: string }) {
  const g = useT().tasks.ziele;
  const ct = useT().common;
  const [goals, setGoals] = useState<ProjectGoals | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [fleetSaving, setFleetSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<ProjectGoals>(`/projects/${projectId}/goals`)
      .then((g) => {
        if (alive) {
          setGoals(g);
          setDraft(g.goal_md ?? "");
        }
      })
      .catch(() => {
        if (alive) setGoals(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}`, { goal_md: draft });
      setGoals((g) => (g ? { ...g, goal_md: draft.trim() || null } : g));
      setEditing(false);
    } catch {
      /* Fehler bleibt still — draft bleibt erhalten zum Retry */
    } finally {
      setSaving(false);
    }
  };

  // Fleet-Board-Switch (Migration 0072): steuert die Loop-Flotte remote.
  // Der Watchdog liest fleet_enabled je Projekt; hier togglen wir es per
  // PATCH — optimistisches Update, bei Fehler Rollback auf den Vorwert.
  const toggleFleet = async (next: boolean) => {
    if (!goals || fleetSaving) return;
    setFleetSaving(true);
    setGoals((g) => (g ? { ...g, fleet_enabled: next } : g));
    try {
      await api.patch(`/projects/${projectId}`, { fleet_enabled: next });
    } catch {
      // Rollback bei Fehler — kein Browser-Dialog, stiller Revert.
      setGoals((g) => (g ? { ...g, fleet_enabled: !next } : g));
    } finally {
      setFleetSaving(false);
    }
  };

  if (loading)
    return (
      <div className="text-xs text-ink-50/50 flex items-center gap-1.5">
        <Loader2 className="w-3 h-3 animate-spin" /> {g.laden}
      </div>
    );
  if (!goals) return null;

  return (
    <div className="rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900/40 p-3 space-y-3 text-sm">
      {/* Fleet-Board-Switch (Migration 0072): remote An/Aus fuer die Loop-Flotte.
          Gemeinsame Komponente (auch im Projekt-Header) — hier controlled, weil
          das Panel den Wert schon aus /goals haelt (kein Doppel-Fetch). */}
      <FleetToggle enabled={goals.fleet_enabled} onToggle={toggleFleet} saving={fleetSaving} />

      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-ink-50/50">
            {g.projektZiel}
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-[11px] text-brand-600 dark:text-brand-400 hover:underline"
            >
              {g.bearbeiten}
            </button>
          )}
        </div>
        {editing ? (
          <div className="mt-1 space-y-1.5">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              placeholder={g.platzhalter}
              aria-label={g.projektZiel}
              className="w-full px-2 py-1.5 rounded border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-sm resize-y"
            />
            <div className="flex gap-2">
              <button
                onClick={save}
                disabled={saving}
                className="text-xs px-2.5 py-1 rounded bg-brand-600 text-white disabled:opacity-50"
              >
                {ct.speichern}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setDraft(goals.goal_md ?? "");
                }}
                className="text-xs px-2.5 py-1 rounded text-ink-50/60 hover:text-ink"
              >
                {ct.abbrechen}
              </button>
            </div>
          </div>
        ) : goals.goal_md ? (
          <p className="mt-0.5 text-ink-50/80 whitespace-pre-wrap">{goals.goal_md}</p>
        ) : (
          <p className="mt-0.5 text-ink-50/40 italic">{g.keinZiel}</p>
        )}
      </div>

      {goals.architecture_md && (
        <details className="group/arch">
          <summary className="text-[11px] uppercase tracking-wider text-ink-50/50 cursor-pointer select-none hover:text-ink-50/70 list-none flex items-center gap-1">
            <ChevronRight className="w-3 h-3 group-open/arch:rotate-90 transition-transform" />
            {g.architektur}
          </summary>
          <pre className="mt-1 text-xs text-ink-50/75 whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-auto">
            {goals.architecture_md}
          </pre>
        </details>
      )}

      {goals.workstreams.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-50/50">
            {g.workstreams}
          </div>
          <ul className="mt-1 space-y-1.5">
            {goals.workstreams.map((w) => (
              <li key={w.phase_id} className="flex gap-2">
                <span className="text-ink-50/30 shrink-0">▸</span>
                <span className="min-w-0">
                  <span className="font-medium text-text">{w.name}</span>
                  {w.overarching_goal ? (
                    <span className="text-ink-50/70"> — {w.overarching_goal}</span>
                  ) : (
                    <span className="text-ink-50/40 italic">{g.workstreamOhneZiel}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Lane({
  lane, columns, isDesktop, liveSessions, sortable = false, dimmed = false,
  onCardClick, onOpenCreate, onIntakeChanged, onWorkstreamContextOpen,
  isWsOpen, onToggleWs,
}: {
  lane: KanbanLane;
  columns: ColumnId[];
  isDesktop: boolean;
  liveSessions?: AgentSessionLive[];
  // sortable: diese Lane ist per DnD (Grip-Handle) umsortierbar (lokale, echte
  // Projekt-Lane). dimmed: sie ist gerade die aktiv gezogene Lane.
  sortable?: boolean;
  dimmed?: boolean;
  onCardClick: (c: KanbanCard) => void;
  onOpenCreate: (project_id: string | null, phase_id: string | null, column: ColumnId) => void;
  // Nach POST (neue Idee) / PATCH (Antworten) einer Intake-Karte: Board neu laden.
  onIntakeChanged: () => void;
  onWorkstreamContextOpen: (phase_id: string) => void;
  isWsOpen: (phase_id: string | null) => boolean;
  onToggleWs: (phase_id: string) => void;
}) {
  const t = useT();
  const k = t.tasks.kanban;
  // Mobile: aktive Tab pro Lane. Default = erste Arbeits-Spalte (nicht „ingest",
  // das ist die Rohsprache-Einwurf-Spalte) — also „todo", falls vorhanden.
  const [activeCol, setActiveCol] = useState<ColumnId>(
    columns.find((c) => c !== "ingest") ?? columns[0] ?? "todo"
  );
  // Ziele-Panel (Migration 0060): lazy aufklappbar unter dem Projekt.
  const [goalsOpen, setGoalsOpen] = useState(false);
  const showGoals =
    lane.project_id !== "__orphan__" && lane.project_source !== "salesforce";

  // Lane-Reorder (Projekt-Prioritaet per DnD): die ganze Lane ist der Sortable-
  // Node (für sauberes vertikales Verschieben), aber NUR der Grip-Handle traegt
  // die Drag-Listener — so kollidiert der Lane-Drag nicht mit Card-Drags/Klicks
  // in den Spalten. hooks werden unbedingt aufgerufen (Rules of Hooks); bei
  // sortable=false bleiben sie ungenutzt.
  const laneSortable = useSortable({ id: laneDndId(lane.project_id), disabled: !sortable });
  const laneRef = sortable ? laneSortable.setNodeRef : undefined;
  const laneStyle: React.CSSProperties = sortable
    ? { transform: CSS.Transform.toString(laneSortable.transform), transition: laneSortable.transition }
    : {};
  const dragHandle = sortable ? (
    <button
      type="button"
      ref={laneSortable.setActivatorNodeRef}
      {...laneSortable.listeners}
      {...laneSortable.attributes}
      className="shrink-0 -ml-1 mt-0.5 p-0.5 rounded text-ink-50/30 hover:text-brand-600 dark:hover:text-brand-400 cursor-grab active:cursor-grabbing touch-none"
      title={k.projektUmsortieren}
      aria-label={k.projektUmsortierenAria(projectDisplayName(lane.project_name))}
    >
      <GripVertical className="w-3.5 h-3.5" />
    </button>
  ) : null;

  const laneHeader = (
    <div className="flex items-start gap-1">
      {dragHandle}
      <div className="min-w-0 flex-1">
      {lane.project_id !== "__orphan__" ? (
        <Link
          to={`/projects/${lane.project_id}`}
          className="text-base font-medium text-ink hover:text-brand-600 dark:hover:text-brand-400 line-clamp-2 flex items-start gap-1.5"
        >
          {lane.project_source === "salesforce" && <Lock className="w-3 h-3 mt-1 shrink-0 text-ink-50/40" />}
          <span>{projectDisplayName(lane.project_name)}</span>
          {lane.project_priority !== 50 && (
            <PriorityChip priority={lane.project_priority} />
          )}
        </Link>
      ) : (
        <span className="text-sm italic text-ink-50/50">{projectDisplayName(lane.project_name)}</span>
      )}
      {showGoals && (
        <button
          onClick={() => setGoalsOpen((o) => !o)}
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-50/60 hover:text-brand-600 dark:hover:text-brand-400"
          aria-expanded={goalsOpen}
          aria-label={k.zieleEinAusklappen}
        >
          {goalsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <Target className="w-3 h-3" />
          {k.ziele}
        </button>
      )}
      {(() => {
        // Tote Sessions (kein Fortschritt + >60 min still) ganz ausblenden.
        const visible = (liveSessions ?? []).filter((s) => !isSessionDead(s));
        if (visible.length === 0) return null;
        return (
          <div className="mt-1.5 space-y-1">
            {visible.map((s) => <LiveSessionCard key={s.id} session={s} />)}
          </div>
        );
      })()}
      </div>
    </div>
  );

  const columnFor = (col: ColumnId) => (
    <Column
      key={col}
      laneId={lane.project_id}
      // Echte Projekt-Id (nicht "__orphan__") — nur so kann die Ingest-Spalte
      // eine Idee einwerfen (POST /projects/{id}/intakes).
      projectId={lane.project_id !== "__orphan__" ? lane.project_id : null}
      col={col}
      cards={lane.cards[col]}
      onCardClick={onCardClick}
      onIntakeChanged={onIntakeChanged}
      onOpenCreate={
        lane.project_source === "salesforce"
          ? undefined  // can't add local tasks to SF project lanes
          : (phase_id) => onOpenCreate(
              lane.project_id === "__orphan__" ? null : lane.project_id,
              phase_id,
              col,
            )
      }
      showWorkstreamHeaders={
        lane.project_id !== "__orphan__" && lane.project_source !== "salesforce"
      }
      onWorkstreamContextOpen={onWorkstreamContextOpen}
      isWsOpen={isWsOpen}
      onToggleWs={onToggleWs}
    />
  );

  // WICHTIG: nur EINE Variante rendern (nicht beide via CSS `hidden`), sonst
  // registriert dnd-kit doppelte Droppable-/Sortable-Ids (die versteckte Kopie
  // misst 0×0 und überschreibt die echten Rects → Drops schlagen fehl).
  if (isDesktop) {
    return (
      <div
        ref={laneRef}
        style={laneStyle}
        className={clsx(
          "border-t border-cream-200 dark:border-ink-700",
          dimmed && "opacity-40",
        )}
      >
        <div
          className="grid py-1"
          style={{ gridTemplateColumns: `200px repeat(${columns.length}, minmax(260px, 1fr))` }}
        >
          <div className="px-2 py-2 sticky left-0 bg-cream-50 dark:bg-ink-900 z-[1]">
            {laneHeader}
          </div>
          {columns.map(columnFor)}
        </div>
        {showGoals && goalsOpen && (
          <div className="px-2 pb-2 sticky left-0 max-w-3xl">
            <GoalsPanel projectId={lane.project_id} />
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Mobile: Lane-Header full-width + Tab-Bar (Todo/In-Progress/Done) + nur aktive Spalte */}
      <div
        ref={laneRef}
        style={laneStyle}
        className={clsx(
          "border-t border-cream-200 dark:border-ink-700 py-2 space-y-2",
          dimmed && "opacity-40",
        )}
      >
        <div className="px-2">{laneHeader}</div>
        {showGoals && goalsOpen && (
          <div className="px-2">
            <GoalsPanel projectId={lane.project_id} />
          </div>
        )}
        <div
          className="flex gap-1 px-1.5 border-b border-cream-200 dark:border-ink-700"
          role="tablist"
          aria-label={k.statusReiterAria(lane.project_name)}
        >
          {columns.map((c) => {
            const count = lane.cards[c].length;
            const isActive = c === activeCol;
            return (
              <button
                key={c}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveCol(c)}
                className={clsx(
                  "flex-1 min-h-[44px] px-2 py-2 text-xs font-medium border-b-2 -mb-px transition-colors flex items-center justify-center gap-1.5",
                  isActive
                    ? "border-brand-500 text-brand-700 dark:text-brand-300"
                    : "border-transparent text-ink-50/60 hover:text-ink"
                )}
              >
                <span>{columnLabels(t)[c]}</span>
                <span
                  className={clsx(
                    "text-[10px] font-mono font-semibold rounded-full px-1.5 py-0.5 min-w-[20px]",
                    isActive
                      ? "bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200"
                      : "bg-cream-200 text-ink-50/70 dark:bg-ink-700"
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {columnFor(activeCol)}
      </div>
    </>
  );
}

// Group cards by phase_id preserving the order the backend gave us
// (which is already ph.priority, ph.position, t.priority, sort_position).
// Cards without a workstream end up in a final "—" group.
// Migration 0036: phase.priority kommt nicht direkt im KanbanCard mit —
// wir holen sie lazy on demand wenn die Sektion expanded ist. Fuer den
// Header-Chip-Sort genuegt die Backend-Reihenfolge.
function groupByWorkstream(cards: KanbanCard[]): { phase_id: string | null; phase_name: string | null; phase_priority: number | null; items: KanbanCard[] }[] {
  const groups: { phase_id: string | null; phase_name: string | null; phase_priority: number | null; items: KanbanCard[] }[] = [];
  let current: typeof groups[number] | null = null;
  for (const c of cards) {
    const pid = c.phase_id ?? null;
    if (!current || current.phase_id !== pid) {
      current = {
        phase_id: pid,
        phase_name: c.phase_name ?? null,
        phase_priority: c.phase_priority ?? null,
        items: [],
      };
      groups.push(current);
    }
    current.items.push(c);
  }
  return groups;
}

function Column({
  laneId, projectId, col, cards, onCardClick, onOpenCreate, onIntakeChanged,
  showWorkstreamHeaders, onWorkstreamContextOpen, isWsOpen, onToggleWs,
}: {
  laneId: string;
  projectId: string | null;
  col: ColumnId;
  cards: KanbanCard[];
  onCardClick: (c: KanbanCard) => void;
  onOpenCreate?: (phase_id: string | null) => void;
  onIntakeChanged: () => void;
  showWorkstreamHeaders: boolean;
  onWorkstreamContextOpen?: (phase_id: string) => void;
  isWsOpen?: (phase_id: string | null) => boolean;
  onToggleWs?: (phase_id: string) => void;
}) {
  const k = useT().tasks.kanban;
  const { setNodeRef, isOver } = useDroppable({ id: `${laneId}__${col}` });

  // „Ingest"-Spalte: Rohsprache-Intakes statt Aufgaben. Karten sind NICHT
  // draggable (agent/antwort-getrieben) und kein „+ Aufgabe"-Wizard — stattdessen
  // ein „+ Idee einwerfen"-Composer im Kopf. Der Droppable bleibt registriert,
  // aber handleDragEnd verwirft jeden Task-Drop in diese Spalte (destCol==="ingest").
  if (col === "ingest") {
    return (
      <IngestColumn
        setNodeRef={setNodeRef}
        isOver={isOver}
        projectId={projectId}
        cards={cards}
        onIntakeChanged={onIntakeChanged}
      />
    );
  }

  const groups = showWorkstreamHeaders ? groupByWorkstream(cards) : null;
  // Header zeigen sobald mindestens 1 echter Workstream Tasks hat — auch
  // wenn nur EIN Workstream existiert. Reine Orphan-Cards (alle phase_id
  // null) bleiben ohne Header damit "ohne Workstream"-Label keine leere
  // Pseudo-Section wird.
  const hasAnyWorkstream = groups && groups.some((g) => g.phase_id !== null);
  const wsOpen = isWsOpen ?? (() => true);
  // SortableContext bekommt NUR die Karten sichtbarer (offener) Gruppen — Karten
  // in zugeklappten Workstreams werden nicht gerendert, dann duerfen sie auch nicht
  // als Sortable-Items registriert sein (sonst 0×0-Rects wie beim Mobile-Layout-Bug).
  const sortableCards =
    groups && hasAnyWorkstream
      ? groups.filter((g) => wsOpen(g.phase_id)).flatMap((g) => g.items)
      : cards;

  // Workstream-Header-Sortables (AC1): jede echte Workstream-Gruppe bekommt eine
  // per (lane, col) eindeutige "phase:"-Sortable-Id — registriert unabhaengig
  // vom Collapse-Zustand (der Grip-Handle sitzt im Header und ist auch bei
  // eingeklapptem Workstream sichtbar). "ohne Workstream" (phase_id null) ist
  // NICHT reorderbar.
  const sortableItems = [
    ...(groups && hasAnyWorkstream
      ? groups
          .filter((g) => g.phase_id != null)
          .map((g) => phaseDndId(laneId, col, g.phase_id!))
      : []),
    ...sortableCards.map((c) => c.id),
  ];

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "px-1.5 py-1 min-h-[60px] rounded-md transition-colors",
        isOver && "bg-accent-50 dark:bg-accent-900/20 outline outline-2 outline-accent-300"
      )}
    >
      {cards.length === 0 && (
        <div className="text-[10px] text-ink-50/30 italic text-center pt-3">—</div>
      )}
      <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
        {groups && hasAnyWorkstream ? (
          <div className="space-y-3">
            {groups.map((g, gi) => (
              <WorkstreamGroup
                key={g.phase_id ?? `__none__${gi}`}
                laneId={laneId}
                col={col}
                group={g}
                onCardClick={onCardClick}
                onWorkstreamContextOpen={onWorkstreamContextOpen}
                open={wsOpen(g.phase_id)}
                onToggle={onToggleWs}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-1.5">
            {cards.map((c) => <Card key={c.id} card={c} onClick={() => onCardClick(c)} />)}
          </div>
        )}
      </SortableContext>
      {onOpenCreate && (
        <button
          onClick={() => onOpenCreate(null)}
          className="mt-1 w-full text-[10px] text-ink-50/40 hover:text-brand-600 hover:bg-cream-100 dark:hover:bg-ink-800 rounded-md min-h-11 md:min-h-0 md:py-1 flex items-center justify-center gap-1"
        >
          <Plus className="w-3 h-3" /> {k.aufgabeHinzufuegen}
        </button>
      )}
    </div>
  );
}

// „Ingest"-Spalte: zeigt project_intakes als Karten (Datenvertrag kind:"intake").
// Kopf: „+ Idee einwerfen" oeffnet ein zentriertes Modal mit dem geteilten
// IntakeComposer (POST /projects/{id}/intakes). Karten sind aufklappbar — beim
// Oeffnen wird der volle Intake nachgeladen und die Rueckfragen (IntakeQuestions)
// angezeigt. KEINE dnd-Listener (agent/antwort-getrieben, nicht verschiebbar).
// Der Droppable (setNodeRef) bleibt registriert, aber handleDragEnd verwirft
// jeden Task-Drop hierher (destCol==="ingest").
function IngestColumn({
  setNodeRef, isOver, projectId, cards, onIntakeChanged,
}: {
  setNodeRef: (el: HTMLElement | null) => void;
  isOver: boolean;
  projectId: string | null;
  cards: KanbanCard[];
  onIntakeChanged: () => void;
}) {
  const k = useT().tasks.kanban;
  const [composerOpen, setComposerOpen] = useState(false);
  const intakes = cards.filter((c) => c.kind === "intake");
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "px-1.5 py-1 min-h-[60px] rounded-md transition-colors",
        isOver && "bg-cream-100/40 dark:bg-ink-800/40"
      )}
    >
      <div className="space-y-1.5">
        {intakes.length === 0 && (
          <div className="text-[10px] text-ink-50/30 italic text-center pt-3">—</div>
        )}
        {intakes.map((c) => (
          <IntakeKanbanCard key={c.id} card={c} onChanged={onIntakeChanged} />
        ))}
      </div>
      {projectId && (
        <button
          onClick={() => setComposerOpen(true)}
          className="mt-1 w-full text-[10px] text-ink-50/40 hover:text-brand-600 hover:bg-cream-100 dark:hover:bg-ink-800 rounded-md min-h-11 md:min-h-0 md:py-1 flex items-center justify-center gap-1"
        >
          <Plus className="w-3 h-3" /> {k.ideeEinwerfen}
        </button>
      )}
      {composerOpen && projectId && (
        <Modal
          onClose={() => setComposerOpen(false)}
          size="md"
          title={k.ideeEinwerfen}
          icon={<Inbox className="w-4 h-4" />}
        >
          <IntakeComposer
            projectId={projectId}
            compact
            onCreated={() => {
              setComposerOpen(false);
              onIntakeChanged();
            }}
          />
        </Modal>
      )}
    </div>
  );
}

// Eine Intake-Karte im Kanban (nicht draggable). Kopf zeigt Status-Chip + „N
// Fragen"-Badge (open_questions_count) + gekuerzten Titel. Aufgeklappt: laedt den
// vollen Intake nach und rendert das geteilte IntakeQuestions-Panel; Antworten
// (PATCH) laden das Board neu.
function IntakeKanbanCard({
  card, onChanged,
}: {
  card: KanbanCard;
  onChanged: () => void;
}) {
  const k = useT().tasks.kanban;
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState<IntakeOut | null>(null);
  const [loading, setLoading] = useState(false);

  const openQ = card.open_questions_count ?? 0;
  // Der Datenvertrag setzt status = intake_status; intake_status ist der genaue
  // Wert. Fallback auf status, falls nur der da ist.
  const intakeStatus = card.intake_status ?? (card.status as IntakeOut["status"]) ?? "raw";

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !full) {
      setLoading(true);
      try {
        setFull(await api.get<IntakeOut>(`/projects/-/intakes/${card.id}`));
      } catch {
        setFull(null);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="bg-surface dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-md">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-start gap-1.5 px-2 py-2 text-left"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 mt-0.5 text-text-3 shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-text-3 shrink-0" />
        )}
        <span className="flex-1 min-w-0 text-sm leading-snug text-text line-clamp-2">
          {card.title || k.intakeLeer}
        </span>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <IntakeStatusChip status={intakeStatus} />
          {openQ > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-700 dark:text-fuchsia-300">
              {k.intakeFragen(openQ)}
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-3 border-t border-cream-200 dark:border-ink-700 pt-2">
          {loading && (
            <div className="text-xs text-text-2 inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {k.laedt}
            </div>
          )}
          {full && (
            <>
              <div className="text-xs text-text-2 whitespace-pre-wrap leading-relaxed">
                {full.raw_text}
              </div>
              <IntakeQuestions
                intake={full}
                onAnswered={() => {
                  onChanged();
                }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Workstream-Gruppe als Drop-Zone: eine Karte, die hier fallen gelassen wird,
// bekommt phase_id dieser Gruppe (Cross-Workstream-DnD innerhalb des Projekts).
// Droppable-Id kodiert Lane+Spalte+Phase; "none" = „ohne Workstream".
function WorkstreamGroup({
  laneId, col, group, onCardClick, onWorkstreamContextOpen, open = true, onToggle,
}: {
  laneId: string;
  col: ColumnId;
  group: { phase_id: string | null; phase_name: string | null; items: KanbanCard[] };
  onCardClick: (c: KanbanCard) => void;
  onWorkstreamContextOpen?: (phase_id: string) => void;
  open?: boolean;
  onToggle?: (phase_id: string) => void;
}) {
  const k = useT().tasks.kanban;
  const { setNodeRef, isOver } = useDroppable({
    id: `${laneId}__${col}__ws__${group.phase_id ?? "none"}`,
  });
  // "ohne Workstream" (phase_id null) ist nicht collapsible — immer offen.
  const collapsible = group.phase_id != null && !!onToggle;
  const isOpen = collapsible ? open : true;
  // Workstream-Grip (AC1): useSortable UNBEDINGT aufrufen (Rules of Hooks),
  // fuer die „ohne Workstream"-Pseudo-Gruppe disabled. Nur der Grip traegt die
  // dnd-listeners — Header-Klick (Context-Drawer) + Collapse bleiben Klicks. Der
  // Header selbst wird NICHT transformiert (kein Dancing); die schwebende Kopie
  // uebernimmt das DragOverlay. Die Sortable-Id ist per (lane, col) eindeutig.
  const phaseSortable = useSortable({
    id: group.phase_id ? phaseDndId(laneId, col, group.phase_id) : `__nows__${laneId}__${col}`,
    disabled: group.phase_id == null,
  });
  const reorderable = group.phase_id != null;
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "rounded-lg border overflow-hidden transition-colors",
        isOver
          ? "bg-accent-50 dark:bg-accent-900/20 border-accent-300 outline outline-2 outline-accent-300"
          : "bg-brand-50/40 dark:bg-brand-900/10 border-brand-200/50 dark:border-brand-800/30"
      )}
    >
      {/* Workstream-Header: Collapse-Chevron + Name (oeffnet Context-Drawer) + Anzahl.
          Default zugeklappt; Zustand pro phase_id in localStorage gemerkt. */}
      <div
        ref={reorderable ? phaseSortable.setNodeRef : undefined}
        className="px-2 py-1.5 bg-brand-100/60 dark:bg-brand-900/30 border-b border-brand-200/50 dark:border-brand-800/40 flex items-center gap-1"
      >
        {reorderable && (
          <button
            type="button"
            ref={phaseSortable.setActivatorNodeRef}
            {...phaseSortable.listeners}
            {...phaseSortable.attributes}
            className="shrink-0 -ml-0.5 p-0.5 rounded text-brand-500/50 hover:text-brand-700 dark:hover:text-brand-300 cursor-grab active:cursor-grabbing touch-none"
            title={k.workstreamUmsortieren}
            aria-label={k.workstreamUmsortierenAria(group.phase_name ?? "")}
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
        )}
        {collapsible && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle!(group.phase_id!); }}
            className="shrink-0 text-brand-700 dark:text-brand-300 hover:text-brand-900 dark:hover:text-brand-100 -ml-0.5"
            aria-expanded={isOpen}
            aria-label={isOpen ? k.workstreamEinklappen : k.workstreamAusklappen}
          >
            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        )}
        {group.phase_id && onWorkstreamContextOpen ? (
          <button
            onClick={(e) => { e.stopPropagation(); onWorkstreamContextOpen(group.phase_id!); }}
            className="text-[11px] uppercase tracking-wide font-bold text-brand-800 dark:text-brand-200 truncate flex-1 text-left hover:text-brand-900 dark:hover:text-brand-100 hover:underline"
            title={k.workstreamKontextOeffnen}
          >
            {group.phase_name ?? k.ohneWorkstream}
          </button>
        ) : (
          <span className="text-[11px] uppercase tracking-wide font-bold text-brand-800 dark:text-brand-200 truncate flex-1">
            {group.phase_name ?? k.ohneWorkstream}
          </span>
        )}
        <span className="text-[10px] font-semibold text-brand-700 dark:text-brand-300 bg-surface/60 dark:bg-ink-900/60 rounded-full px-1.5 py-0.5 shrink-0">
          {group.items.length}
        </span>
      </div>
      {isOpen && (
        <div className="space-y-1.5 px-1.5 py-1.5">
          {group.items.map((c) => <Card key={c.id} card={c} onClick={() => onCardClick(c)} />)}
        </div>
      )}
    </div>
  );
}

// Klein, mit Zahl — fuer Lane- und Workstream-Header. Migration 0036.
function PriorityChip({ priority }: { priority: number }) {
  const k = useT().tasks.kanban;
  const p = priority;
  const cls =
    p <= 25
      ? "bg-danger-soft text-danger"
      : p <= 60
      ? "bg-warning-soft text-warning"
      : "bg-surface-3 text-text-2";
  return (
    <span
      className={clsx("text-[10px] font-mono font-semibold px-1 py-0.5 rounded-md shrink-0", cls)}
      title={k.prioritaetTitel(p)}
    >
      P{p}
    </span>
  );
}

// Presentational — Karten-Inhalt (Karten-Diaet S3): Status-Punkt + Titel +
// optional Fälligkeit. Wird sowohl von der sortierbaren <Card/> als auch vom
// DragOverlay (schwebende Kopie) genutzt. `overlay` gibt der Kopie Schatten +
// grabbing-Cursor.
function CardView({ card, overlay = false }: { card: KanbanCard; overlay?: boolean }) {
  const k = useT().tasks.kanban;
  const { username } = useAuth();
  const isOverdue = card.due_date && daysUntil(card.due_date)! < 0 && card.column !== "done";
  const dueDays = daysUntil(card.due_date);
  const today = new Date().toISOString().slice(0, 10);
  const remActive =
    (!!card.reminder_next_on && card.reminder_next_on <= today) ||
    card.reminder_last_fired_on === today;
  // Zuweisungs-Hinweis: an mich (von X) vs. von mir vergeben (→ Y).
  const assignedToMe = !!card.created_by && card.created_by !== card.owner && card.owner === username;
  const assignedByMe = !!card.created_by && card.created_by === username && card.owner !== username;
  return (
    <div
      className={clsx(
        "w-full text-left border rounded-md p-2 transition-colors duration-150",
        card.read_only
          ? "bg-cream-100 dark:bg-ink-800 border-cream-200 dark:border-ink-700 opacity-90"
          : "bg-surface dark:bg-ink-800 border-cream-200 dark:border-ink-600 group-hover:border-brand-300 dark:group-hover:border-brand-700",
        overlay && "shadow-lg ring-2 ring-accent-300 cursor-grabbing rotate-1"
      )}
    >
      <div className="flex items-start gap-1.5">
        {card.source === "salesforce" && <Lock className="w-3 h-3 mt-0.5 shrink-0 text-text-3" />}
        {card.source === "local" && (
          <span className="w-2 h-2 mt-1.5 rounded-full shrink-0 bg-text-3" aria-hidden />
        )}
        <span className="flex-1 min-w-0 text-sm leading-snug text-text">{card.title}</span>
        {card.due_date && (
          <span
            className={clsx(
              "shrink-0 flex items-center gap-0.5 mt-0.5 text-[10px] tabular-nums",
              isOverdue
                ? "text-danger font-medium"
                : dueDays !== null && dueDays <= 7
                ? "text-warning"
                : "text-text-3"
            )}
            title={isOverdue ? k.ueberfaellig : k.faelligAm}
          >
            {isOverdue && <AlertCircle className="w-2.5 h-2.5" />}
            {card.due_date}
          </span>
        )}
        {card.reminder_next_on && (
          <span
            className={clsx(
              "shrink-0 flex items-center gap-0.5 mt-0.5 text-[10px] tabular-nums",
              remActive ? "text-amber-600 dark:text-amber-400 font-medium" : "text-text-3"
            )}
            title={k.erinnerungAm(card.reminder_next_on)}
          >
            <Bell className="w-2.5 h-2.5" />
            {card.reminder_next_on}
          </span>
        )}
        {card.sf_url && (
          <a
            href={card.sf_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-text-3 hover:text-brand-600 shrink-0 flex items-center justify-center min-h-11 min-w-11 md:min-h-0 md:min-w-0"
            title={k.inSalesforceOeffnen}
            aria-label={k.inSalesforceOeffnen}
          >
            <ExternalLink className="w-4 h-4 md:w-3 md:h-3" />
          </a>
        )}
      </div>
      {(assignedToMe || assignedByMe) && (
        <div className="mt-1 pl-3.5 text-[10px] text-text-3 inline-flex items-center gap-1">
          <Users className="w-2.5 h-2.5 shrink-0" />
          {assignedToMe
            ? k.zugewiesenVon(card.created_by ?? "")
            : k.zugewiesenAn(card.owner ?? "")}
        </div>
      )}
      {/* Offene ask-Rueckfrage (status='feedback', keine Eskalation): der User
          muss im Drawer eine A/B-Frage beantworten. Ohne diesen Badge landet die
          Task per _STATUS_TO_COLUMN in "In Arbeit" und wirkt, als arbeite noch
          jemand — der User sieht nicht, dass sie auf IHN wartet. */}
      {card.status === "feedback" && !card.needs_human && (
        <div className="mt-1 pl-3.5 text-[10px] text-fuchsia-700 dark:text-fuchsia-300 font-medium inline-flex items-center gap-1">
          <HelpCircle className="w-2.5 h-2.5 shrink-0" />
          {k.wartetAufAntwort}
        </div>
      )}
      {/* Decider-Eskalation (Migration 0058): braucht menschliche Entscheidung. */}
      {card.needs_human && (
        <div className="mt-1 pl-3.5 text-[10px] text-danger font-medium inline-flex items-center gap-1">
          <AlertCircle className="w-2.5 h-2.5 shrink-0" />
          {k.brauchtEntscheidung}
        </div>
      )}
      {/* Rework nach Reviewer-Gate: Task war schon im Review + kam zurück. */}
      {!card.needs_human && card.review_round > 0 && card.column !== "done" && (
        <div className="mt-1 pl-3.5 text-[10px] text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
          <AlertCircle className="w-2.5 h-2.5 shrink-0" />
          {k.rework(card.review_round)}
        </div>
      )}
      {/* Dependency-Gating (Migration 0059): wartet auf noch nicht fertige Vorgänger. */}
      {card.blocked && card.column === "todo" && (
        <div className="mt-1 pl-3.5 text-[10px] text-text-3 inline-flex items-center gap-1">
          <Lock className="w-2.5 h-2.5 shrink-0" />
          {k.wartetAufTasks(card.depends_on.length)}
        </div>
      )}
    </div>
  );
}

function Card({ card, onClick }: { card: KanbanCard; onClick: () => void }) {
  // SF-Karten (read_only) sind nicht draggable. Lokale Karten: useSortable
  // erlaubt Reorder (Drop auf Karte), Cross-Column (Drop auf Spalte) und
  // Cross-Workstream (Drop auf Gruppe). Ganze Karte = Drag-Handle (listeners
  // auf dem Button). Touch startet erst nach 150ms Halten (TouchSensor), damit
  // Mobile-Scrollen nicht bricht.
  const { attributes, listeners, setNodeRef, isDragging, transform, transition } = useSortable({
    id: card.id,
    disabled: card.read_only,
  });
  const dragStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (card.read_only) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group w-full cursor-pointer rounded-md"
      >
        <CardView card={card} />
      </button>
    );
  }
  return (
    <button
      type="button"
      ref={setNodeRef}
      style={dragStyle}
      {...listeners}
      {...attributes}
      onClick={() => {
        if (!isDragging) onClick();
      }}
      className={clsx(
        "group w-full rounded-md cursor-grab active:cursor-grabbing touch-none",
        isDragging && "opacity-40"
      )}
    >
      <CardView card={card} />
    </button>
  );
}

// WorkstreamContextDrawer — zentriertes Modal mit Markdown-Editor fuer
// phases.context_md (Migration 0035). Loop-Sessions lesen das in PERCEIVE,
// aktualisieren in WRAP-UP. UI: Editor links, Live-Vorschau rechts, Linecount-
// Badge mit Warn-Farbe bei >80 Zeilen (Soft-Budget gegen Sprawl).
const WORKSTREAM_CTX_LINE_BUDGET = 80;
const WORKSTREAM_CTX_TEMPLATE = `# Workstream: <Name>

## Overarching Goal
<1-3 Saetze — was am Ende des Workstreams da sein soll>

## Aktive Entscheidungen
- <Decision X — Grund>

## Verworfen / Gelernt
- <Pattern Z versucht in Task #<id>, abgebrochen weil …>

## Konventionen
- <Tone, Library-Wahl, Naming, …>

## Aktueller Stand
<2-4 Saetze — wo stehen wir, was ist next>
`;

interface PhaseFull {
  id: string;
  project_id: string;
  name: string;
  position: number;
  priority: number;
  context_md: string | null;
  created_at: string;
  updated_at: string;
}

interface Handoff {
  id: string;
  phase_id: string;
  project_id: string | null;
  content: string;
  next_action: string | null;
  author: string;
  created_at: string;
}

// Handoff-Historie eines Workstreams (Migration 0061). Read-only — Handoffs
// schreibt der Loop im WRAP-UP, nicht der User hier.
function HandoffsSection({ phaseId }: { phaseId: string }) {
  const d = useT().tasks.workstreamDoku;
  const [handoffs, setHandoffs] = useState<Handoff[] | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .get<Handoff[]>(`/phases/${phaseId}/handoffs`)
      .then((h) => { if (alive) setHandoffs(h); })
      .catch(() => { if (alive) setHandoffs([]); });
    return () => { alive = false; };
  }, [phaseId]);
  if (!handoffs || handoffs.length === 0) return null;
  return (
    <div className="px-4 py-2.5 border-t border-cream-200 dark:border-ink-700 max-h-52 overflow-auto">
      <div className="text-[11px] uppercase tracking-wider text-ink-50/50 mb-1.5">
        {d.handoffs(handoffs.length)}
      </div>
      <ul className="space-y-2.5">
        {handoffs.map((h) => (
          <li key={h.id} className="text-xs border-l-2 border-brand-200 dark:border-brand-800 pl-2">
            <div className="flex items-center gap-1.5 text-ink-50/50 text-[10px]">
              <span>{h.created_at.slice(0, 16).replace("T", " ")}</span>
              <span>·</span>
              <span>{h.author}</span>
            </div>
            {h.next_action && (
              <div className="text-brand-700 dark:text-brand-300 font-medium mt-0.5">
                → {h.next_action}
              </div>
            )}
            <div className="text-ink-50/70 whitespace-pre-wrap line-clamp-4 mt-0.5">{h.content}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WorkstreamContextDrawer({
  phaseId,
  onClose,
}: {
  phaseId: string | null;
  onClose: () => void;
}) {
  const d = useT().tasks.workstreamDoku;
  const ct = useT().common;
  const modal = useModal();
  const [phase, setPhase] = useState<PhaseFull | null>(null);
  const [draft, setDraft] = useState("");
  const [priorityDraft, setPriorityDraft] = useState<number>(50);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Single source of truth: zentriertes Custom-Modal (statt window.confirm,
  // siehe Memory feedback_no_browser_dialogs). Schliesst nur, wenn entweder
  // nichts dirty ist oder der User aktiv "Verwerfen" klickt.
  const confirmCloseIfDirty = async (): Promise<boolean> => {
    if (!dirty) return true;
    return modal.confirm({
      title: d.verwerfenFrage,
      description: d.verwerfenText,
      confirmLabel: ct.verwerfen,
      destructive: true,
    });
  };
  const tryClose = async () => {
    if (await confirmCloseIfDirty()) onClose();
  };
  // Vorschau-Toggle (S4): es ist immer entweder das Textarea ODER die
  // gerenderte Vorschau sichtbar, nie beides. Default = Bearbeiten.
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!phaseId) { setPhase(null); setDraft(""); setPriorityDraft(50); setDirty(false); setError(null); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    api.get<PhaseFull>(`/phases/${phaseId}`)
      .then((p) => {
        if (cancelled) return;
        setPhase(p);
        setDraft(p.context_md ?? "");
        setPriorityDraft(p.priority ?? 50);
        setDirty(false);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message || "load failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [phaseId]);

  // Esc closes (mit dirty-check)
  useEffect(() => {
    if (!phaseId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        void tryClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phaseId, dirty, onClose]);

  if (!phaseId) return null;

  const save = async () => {
    if (!phase) return;
    setBusy(true); setError(null);
    try {
      const next = await api.patch<PhaseFull>(`/phases/${phase.id}`, {
        context_md: draft.trim().length > 0 ? draft : null,
        priority: priorityDraft,
      });
      setPhase(next);
      setDirty(false);
    } catch (e: any) {
      setError(e?.message || "save failed");
    } finally {
      setBusy(false);
    }
  };

  const insertTemplate = async () => {
    if (draft.trim().length > 0) {
      const ok = await modal.confirm({
        title: d.ueberschreibenFrage,
        confirmLabel: d.ueberschreiben,
        destructive: true,
      });
      if (!ok) return;
    }
    const name = phase?.name ?? d.workstreamNamePlatzhalter;
    setDraft(WORKSTREAM_CTX_TEMPLATE.replace("<Name>", name));
    setDirty(true);
  };

  const lineCount = draft.split("\n").length;
  const overBudget = lineCount > WORKSTREAM_CTX_LINE_BUDGET;

  return (
    <Modal
      onClose={() => void tryClose()}
      size="full"
      className="h-[90vh] md:h-auto"
      bodyClassName="flex-1 flex flex-col overflow-hidden min-h-0"
    >
        {/* Header — auf mobile flex-wrap, damit Title/Priority/Linecount/Close nicht
            quetschen. min-w-0 in der Title-Box damit truncate greift, aber auf
            mobile faellt das Subtitle/Priority unter den Title. */}
        <div className="px-3 md:px-4 py-2 md:py-3 border-b border-cream-200 dark:border-ink-700 flex items-start md:items-center justify-between gap-2 md:gap-3 flex-wrap">
          <div className="flex-1 min-w-0 flex items-start md:items-center gap-2 flex-wrap">
            <Notebook className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5 md:mt-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-ink break-words md:truncate">
                {d.kopf(phase?.name ?? "…")}
              </div>
              <div className="text-[11px] text-ink-50/55 hidden md:block">
                {d.kopfUnterzeile}
              </div>
            </div>
            {/* Priority-Input — Migration 0036. Beeinflusst next-claimable-Cascade. */}
            <div className="flex items-center gap-1.5 shrink-0">
              <label className="text-xs uppercase tracking-wider text-ink-50/50">{d.prio}</label>
              <input
                type="number"
                min={1}
                max={100}
                value={priorityDraft}
                aria-label={d.prioAria}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 100) {
                    setPriorityDraft(v);
                    setDirty(true);
                  }
                }}
                disabled={busy}
                className="w-14 px-2 py-1 rounded-md border border-cream-200 dark:border-ink-600 bg-surface dark:bg-ink-900 text-xs font-mono text-center"
                title={d.prioTitel}
              />
            </div>
          </div>
          <span
            className={
              "text-[11px] font-mono px-2 py-0.5 rounded-md shrink-0 " +
              (overBudget
                ? "bg-warning-soft text-warning"
                : "bg-cream-100 text-ink-50/70 dark:bg-ink-700")
            }
            title={overBudget ? d.budgetUeberschritten : d.budget}
          >
            {d.zeilen(lineCount, WORKSTREAM_CTX_LINE_BUDGET)}
          </span>
          <button
            onClick={() => void tryClose()}
            className="text-ink-50/60 hover:text-ink p-1 rounded-md hover:bg-cream-100 dark:hover:bg-ink-700 shrink-0 min-w-[36px] min-h-[36px] flex items-center justify-center"
            aria-label={d.schliessenAria}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — ein Feld: entweder Textarea ODER Vorschau (S4), nie beide. */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {loading ? (
            <div className="flex-1 flex items-center justify-center text-text-3">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <>
              <div className="px-3 py-1.5 text-xs uppercase tracking-wider text-text-3 bg-surface-2/50 border-b border-border/50 flex items-center justify-between gap-2">
                <span>{showPreview ? d.vorschau : d.markdown}</span>
                <div className="flex items-center gap-1.5">
                  {!showPreview && (!draft || draft.trim().length === 0) && (
                    <button
                      onClick={insertTemplate}
                      className="text-[10px] px-1.5 py-0.5 rounded-md border border-border hover:bg-surface dark:hover:bg-ink-700"
                    >
                      {d.vorlageEinfuegen}
                    </button>
                  )}
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    aria-pressed={showPreview}
                    className="text-[10px] px-1.5 py-0.5 rounded-md border border-border hover:bg-surface dark:hover:bg-ink-700"
                  >
                    {showPreview ? ct.bearbeiten : d.vorschau}
                  </button>
                </div>
              </div>
              {showPreview ? (
                <div className="flex-1 overflow-auto p-3 text-xs leading-relaxed min-h-0">
                  {draft.trim().length > 0 ? (
                    <pre className="whitespace-pre-wrap font-mono text-[12px] m-0">{draft}</pre>
                  ) : (
                    <div className="text-text-3 italic">{d.leer}</div>
                  )}
                </div>
              ) : (
                <textarea
                  value={draft}
                  onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
                  placeholder={d.editorPlatzhalter}
                  aria-label={d.editorAria}
                  className="flex-1 w-full p-3 text-xs font-mono leading-relaxed bg-surface dark:bg-ink-900 text-text resize-none focus:outline-none min-h-[240px]"
                  spellCheck={false}
                />
              )}
            </>
          )}
        </div>

        {/* Handoffs (Migration 0061): Flywheel-Closure-Historie dieses Workstreams. */}
        {phase && <HandoffsSection phaseId={phase.id} />}

        {/* Footer */}
        <div className="px-4 py-3 border-t border-cream-200 dark:border-ink-700 flex items-center justify-between gap-2">
          <div className="text-[11px] text-ink-50/55">
            {error && <span className="text-danger">⚠ {error}</span>}
            {!error && dirty && <span className="text-warning">{d.ungespeichert}</span>}
            {!error && !dirty && phase?.context_md && (
              <span>{d.zuletztGeaendert(phase.updated_at?.slice(0, 16).replace("T", " "))}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => void tryClose()} disabled={busy}>
              {d.schliessen}
            </Button>
            <Button variant="primary" size="sm" onClick={save} disabled={!dirty} loading={busy}>
              {ct.speichern}
            </Button>
          </div>
        </div>
    </Modal>
  );
}

/** Minuten seit dem letzten Heartbeat/Board-Update dieser Session. */
function sessionQuietMin(session: AgentSessionLive): number {
  return Math.max(
    0,
    Math.round((Date.now() - new Date(session.last_heartbeat_at).getTime()) / 60000)
  );
}

/** Session gilt als "stale", wenn seit >20 min kein Heartbeat kam. */
function isSessionStale(session: AgentSessionLive): boolean {
  return sessionQuietMin(session) > 20;
}

/**
 * Tote Sessions ganz ausblenden: kein Fortschritt (ctx=0, 0 done, 0 wall) UND
 * seit >60 min still. Das sind Loops, die nie einen Heartbeat mit echten Werten
 * geschickt haben und nur den Kanban zumuellen.
 */
function isSessionDead(session: AgentSessionLive): boolean {
  return (
    session.context_estimate_pct === 0 &&
    session.tasks_done_count === 0 &&
    session.wall_time_minutes === 0 &&
    sessionQuietMin(session) > 60
  );
}

function LiveSessionCard({ session }: { session: AgentSessionLive }) {
  const s = useT().tasks.session;
  // Atlas-Agent-Loop, der gerade an einer Task in dieser Lane arbeitet.
  // Anzeige: bot + session_name + ctx-fill-bar + suggested_action-badge.
  const colorByAction: Record<AgentSessionLive["suggested_action"], string> = {
    "continue": "bg-success-soft text-success",
    "wrap-up-soon": "bg-warning-soft text-warning",
    "wrap-up-now": "bg-danger-soft text-danger ring-1 ring-danger",
  };
  const barColorByPct = session.context_estimate_pct >= 80
    ? "bg-danger"
    : session.context_estimate_pct >= 60
    ? "bg-warning"
    : "bg-success";
  // Minuten seit letztem Board-Update (Heartbeat kommt nur nach 'done' — dazwischen
  // arbeitet der Agent still weiter; Badge bleibt aber sichtbar bis 'end').
  const quietMin = sessionQuietMin(session);
  const stale = isSessionStale(session);
  return (
    <div
      className={clsx(
        "rounded-md border border-brand-200/60 dark:border-brand-800/60 bg-brand-50/40 dark:bg-brand-950/30 px-1.5 py-1 text-[10px]",
        stale && "opacity-40",
      )}
      title={stale ? s.staleTitel(quietMin) : undefined}
    >
      <div className="flex items-center gap-1">
        <span className="opacity-60">⚙</span>
        <span className="font-mono truncate flex-1">{session.session_name}</span>
        {stale ? (
          <span className="px-1 py-0.5 rounded-md font-medium text-[9px] bg-cream-200 text-ink-500 dark:bg-ink-700 dark:text-cream-300">
            {s.stale}
          </span>
        ) : (
          <span className={clsx("px-1 py-0.5 rounded-md font-medium text-[9px]", colorByAction[session.suggested_action])}>
            {session.suggested_action === "continue" ? s.ok :
             session.suggested_action === "wrap-up-soon" ? s.wrapSoon : s.wrapNow}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <div className="flex-1 h-1 rounded-full bg-cream-200 dark:bg-ink-700 overflow-hidden">
          <div
            className={clsx("h-full transition-[width] duration-300 ease-out", barColorByPct)}
            style={{ width: `${Math.min(100, session.context_estimate_pct)}%` }}
          />
        </div>
        <span className="text-[9px] opacity-60 tabular-nums">{session.context_estimate_pct}%</span>
      </div>
      <div className="flex items-center justify-between text-[9px] opacity-50 mt-0.5">
        <span>{s.erledigt(session.tasks_done_count)}</span>
        <span title={s.letztesUpdate}>
          {stale ? `↻ ${quietMin}m` : quietMin > 1 ? `↻ ${quietMin}m` : s.live} · {session.wall_time_minutes}m
        </span>
      </div>
    </div>
  );
}

/** Direkt-Route-Hülle (Bestand). Der Inhalt lebt in <KanbanBoard/>. */
export default function KanbanPage() {
  return <KanbanBoard />;
}
